import React, { useState, useEffect, useRef, useMemo, useCallback, memo, useDeferredValue } from 'react';
import {
  Page,
  Text,
  Card,
  Button,
  BlockStack,
  Box,
  InlineStack,
  TextField,
  Badge,
  Select,
  Spinner,
  Toast,
  Banner,
} from "@shopify/polaris";
import { useFormik } from 'formik';
import '../assets/index.css';
import PageTitleBar from '../components/PageTitleBar';
import { campaignAPI } from '../services/campaignAPI';
import { useSelector } from 'react-redux';
import { useSafeAppBridge } from '../hooks/useSafeAppBridge';
import { useEnsureInstall } from '../hooks/useEnsureInstall';
import { Redirect } from '@shopify/app-bridge/actions';
import { TAB_OPTIONS, INITIAL_VALUES } from '../helper/commonVariables';
import { decodeHtmlEntitiesDeep, encodeHtmlEntitiesDeep, decodeHtmlEntities, formatMoney } from '../helper/Common';
import { buildNodeApiEntryUrl } from '../config/env';
// Removed GraphQL metafield imports - only used during save, imported dynamically

// Memoized ColorPicker component
const ColorPicker = memo(({ label, value, onChange, pathPrefix, onColorChange }) => (
  <BlockStack gap="200" style={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-start' }}>
    <div style={{ width: '100%', paddingTop: '10px' }}>
      <Text variant="bodyMd" fontWeight="medium">{label}</Text>
    </div>
    <input
      type="color"
      value={value}
      onChange={(e) => onColorChange(`${pathPrefix}.${onChange}`, e.target.value)}
      style={{
        width: '30%',
        height: '40px',
        border: '1px solid #d1d5db',
        borderRadius: '6px',
        cursor: 'pointer'
      }}
      name={`${pathPrefix}_${onChange}`}
    />
  </BlockStack>
));
ColorPicker.displayName = 'ColorPicker';

// Memoized TextField component to prevent unnecessary re-renders
const MemoizedTextField = memo(({ label, value, onChange, helpText, multiline, name, type, min, suffix, onBlur, onKeyUp, required }) => (
  <TextField
    label={label}
    value={value}
    onChange={onChange}
    helpText={helpText}
    multiline={multiline}
    name={name}
    type={type}
    {...(min !== undefined && min !== null ? { min } : {})}
    suffix={suffix}
    onBlur={onBlur}
    onKeyUp={onKeyUp}
    required={required}
  />
));
MemoizedTextField.displayName = 'MemoizedTextField';

// Memoized Select component
const MemoizedSelect = memo(({ label, options, value, onChange, name }) => (
  <Select
    label={label}
    options={options}
    value={value}
    onChange={onChange}
    name={name}
  />
));
MemoizedSelect.displayName = 'MemoizedSelect';

// Memoized Custom CSS and Script section component
const CustomCssScriptSection = memo(({ customCss, customScript, onCssChange, onScriptChange }) => {
  return (
    <div style={{marginBottom: '1rem'}}>
    <Card>
      <Box>
        <BlockStack gap="400">
          <Text variant="headingMd" as="h2">Custom CSS and Script</Text>
          <BlockStack gap="400">
            <BlockStack gap="200">
              <Text variant="bodyMd" fontWeight="medium" as="label" htmlFor="customCss">
                Custom CSS
              </Text>
              <textarea
                id="customCss"
                name="customCss"
                value={customCss || ''}
                onChange={(e) => onCssChange(e.target.value)}
                rows={10}
                style={{
                  width: '100%',
                  height: '110px',
                  padding: '8px 12px',
                  fontSize: '14px',
                  fontFamily: 'monospace',
                  lineHeight: '1.5',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  resize: 'vertical',
                  boxSizing: 'border-box'
                }}
              />
              <Text variant="bodySm" tone="subdued" as="p">
                Add custom CSS styles. These will be injected into the campaign widget.
              </Text>
            </BlockStack>
            <BlockStack gap="200">
              <Text variant="bodyMd" fontWeight="medium" as="label" htmlFor="customScript">
                Custom Script
              </Text>
              <textarea
                id="customScript"
                name="customScript"
                value={customScript || ''}
                onChange={(e) => onScriptChange(e.target.value)}
                rows={10}
                style={{
                  width: '100%',
                  height: '110px',
                  padding: '8px 12px',
                  fontSize: '14px',
                  fontFamily: 'monospace',
                  lineHeight: '1.5',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  resize: 'vertical',
                  boxSizing: 'border-box'
                }}
              />
              <Text variant="bodySm" tone="subdued" as="p">
                Add custom JavaScript code. These will be injected into the campaign widget.
              </Text>
            </BlockStack>
          </BlockStack>
        </BlockStack>
      </Box>
    </Card>
    </div>
  );
}, (prevProps, nextProps) => {
  return prevProps.customCss === nextProps.customCss &&
    prevProps.customScript === nextProps.customScript;
});
CustomCssScriptSection.displayName = 'CustomCssScriptSection';

// Memoized form section component for Crowdfunding
const CrowdfundingFormSection = memo(({ settings, pathPrefix, onFieldChange, onColorChange, onNumberChange, onNumberBlur }) => {
  // Get default values from INITIAL_VALUES
  const defaults = INITIAL_VALUES.crowdfunding;
  
  return (
      <BlockStack gap="500">
        <Card>
          <Box>
            <BlockStack gap="400">
              <BlockStack gap="400">
                <Text variant="headingMd" as="h2">Text Content</Text>
                <BlockStack gap="400">
                  <InlineStack gap="300" wrap={false}>
                    <Box minWidth="0" style={{ flex: 1 }}>
                  <MemoizedTextField
                        label="Raised Amount Text"
                        value={settings.raisedText}
                    onChange={(value) => onFieldChange(`${pathPrefix}.raisedText`, value)}
                        helpText="Use {raised} placeholder"
                        name={`${pathPrefix}_raisedText`}
                      />
                    </Box>
                    <Box minWidth="0" style={{ flex: 1 }}>
                  <MemoizedTextField
                        label="Goal Amount Text"
                        value={settings.goalText}
                    onChange={(value) => onFieldChange(`${pathPrefix}.goalText`, value)}
                        helpText="Use {goal} placeholder"
                        name={`${pathPrefix}_goalText`}
                      />
                    </Box>
                  </InlineStack>
                  <MemoizedTextField
                    label="Progress Message"
                    value={settings.progressMessage}
                onChange={(value) => onFieldChange(`${pathPrefix}.progressMessage`, value)}
                    multiline={3}
                    name={`${pathPrefix}_progressMessage`}
                  />
                  <InlineStack gap="300" wrap={false}>
                    <Box minWidth="0" style={{ flex: 1 }}>
                    <MemoizedTextField
                        label="Funded Percentage Text"
                        value={settings.fundedText || defaults.fundedText || ''}
                    onChange={(value) => onFieldChange(`${pathPrefix}.fundedText`, value)}
                        helpText="Use {percentage} placeholder"
                        name={`${pathPrefix}_fundedText`}
                      />
                    </Box>
                    <Box minWidth="0" style={{ flex: 1 }}>
                    <MemoizedTextField
                        label="Backer Count Text"
                        value={settings.backerText}
                    onChange={(value) => onFieldChange(`${pathPrefix}.backerText`, value)}
                        helpText="Use {backers} placeholder"
                        name={`${pathPrefix}_backerText`}
                      />
                    </Box>
                  </InlineStack>
                  <InlineStack gap="300" wrap={false}>
                    <Box minWidth="0" style={{ flex: 1 }}>
                    <MemoizedTextField
                        label="Time Remaining Text"
                        value={settings.timeLeftText}
                    onChange={(value) => onFieldChange(`${pathPrefix}.timeLeftText`, value)}
                        helpText="Use {days}, {hours}, {minutes} for the timer."
                        name={`${pathPrefix}_timeLeftText`}
                      />
                    </Box>
                  </InlineStack>
              
                </BlockStack>
              </BlockStack>

              <BlockStack gap="400">
                <Box paddingBlockStart="300" paddingBlockEnd="300">
                  <div style={{ height: '1px', backgroundColor: '#e1e5e9', width: '100%' }} />
                </Box>
                <Text variant="headingMd" as="h2">Layout & Style</Text>
                <InlineStack gap="300" wrap={false}>
                  <Box minWidth="0" style={{ flex: 1 }}>
                  <MemoizedTextField
                      label="Widget Border Radius"
                      type="number"
                      min="0"
                      value={settings.widgetBorderRadius !== null && settings.widgetBorderRadius !== undefined ? settings.widgetBorderRadius.toString() : ''}
                  onChange={(value) => onNumberChange(`${pathPrefix}.widgetBorderRadius`, value)}
                  onBlur={(e) => onNumberBlur(`${pathPrefix}.widgetBorderRadius`, e.target.value)}
                      onKeyUp={(e) => {
                        if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                      onNumberChange(`${pathPrefix}.widgetBorderRadius`, e.target.value);
                        }
                      }}
                      suffix="px"
                      name={`${pathPrefix}_widgetBorderRadius`}
                    />
                  </Box>
                </InlineStack>
              </BlockStack>

              <BlockStack gap="400">
                <Box paddingBlockStart="300" paddingBlockEnd="300">
                  <div style={{ height: '1px', backgroundColor: '#e1e5e9', width: '100%' }} />
                </Box>
                <Text variant="headingMd" as="h2">Typography</Text>
                <BlockStack gap="300">
                  <InlineStack gap="300" wrap={false}>
                    <Box minWidth="0" style={{ flex: 1 }}>
                      <MemoizedTextField
                        label="Header Font Size (px)"
                        type="number"
                        value={settings.headerFontSize !== null && settings.headerFontSize !== undefined && settings.headerFontSize !== '' ? settings.headerFontSize.toString() : ''}
                        onChange={(value) => {
                          // Allow empty string during typing
                          if (value === '' || value === null || value === undefined) {
                            onNumberChange(`${pathPrefix}.headerFontSize`, '');
                            return;
                          }
                          const parsed = parseInt(value);
                          // Allow 0, 1, 2, etc. - only use default if NaN
                          const numValue = isNaN(parsed) ? null : parsed;
                          onNumberChange(`${pathPrefix}.headerFontSize`, numValue);
                        }}
                        onBlur={(e) => {
                          const value = e.target.value;
                          // Validate: field cannot be blank
                          if (value === '' || value === null || value === undefined) {
                            onNumberBlur(`${pathPrefix}.headerFontSize`, '');
                            return;
                          }
                          const parsed = parseInt(value);
                          // If NaN, keep empty to show validation error
                          const numValue = isNaN(parsed) ? null : parsed;
                          onNumberBlur(`${pathPrefix}.headerFontSize`, numValue);
                        }}
                        required
                        suffix="px"
                        helpText="Applies to: Demo Funding title, Reward Title"
                        name={`${pathPrefix}_headerFontSize`}
                      />
                    </Box>
                    <Box minWidth="0" style={{ flex: 1 }}>
                      <MemoizedTextField
                        label="Primary Text Font Size (px)"
                        type="number"
                        value={settings.primaryFontSize !== null && settings.primaryFontSize !== undefined && settings.primaryFontSize !== '' ? settings.primaryFontSize.toString() : ''}
                        onChange={(value) => {
                          // Allow empty string during typing
                          if (value === '' || value === null || value === undefined) {
                            onNumberChange(`${pathPrefix}.primaryFontSize`, '');
                            return;
                          }
                          const parsed = parseInt(value);
                          // Allow 0, 1, 2, etc. - only use default if NaN
                          const numValue = isNaN(parsed) ? null : parsed;
                          onNumberChange(`${pathPrefix}.primaryFontSize`, numValue);
                        }}
                        onBlur={(e) => {
                          const value = e.target.value;
                          // Validate: field cannot be blank
                          if (value === '' || value === null || value === undefined) {
                            onNumberBlur(`${pathPrefix}.primaryFontSize`, '');
                            return;
                          }
                          const parsed = parseInt(value);
                          // If NaN, keep empty to show validation error
                          const numValue = isNaN(parsed) ? null : parsed;
                          onNumberBlur(`${pathPrefix}.primaryFontSize`, numValue);
                        }}
                        required
                        suffix="px"
                        helpText="Applies to: Raised amount, Goal amount, Funded percentage, Backers count"
                        name={`${pathPrefix}_primaryFontSize`}
                      />
                    </Box>
                  </InlineStack>
                  <InlineStack gap="300" wrap={false}>
                    <Box minWidth="0" style={{ flex: 1 }}>
                      <MemoizedTextField
                        label="Secondary Text Font Size (px)"
                        type="number"
                        value={settings.secondaryFontSize !== null && settings.secondaryFontSize !== undefined && settings.secondaryFontSize !== '' ? settings.secondaryFontSize.toString() : ''}
                        onChange={(value) => {
                          // Allow empty string during typing
                          if (value === '' || value === null || value === undefined) {
                            onNumberChange(`${pathPrefix}.secondaryFontSize`, '');
                            return;
                          }
                          const parsed = parseInt(value);
                          // Allow 0, 1, 2, etc. - only use default if NaN
                          const numValue = isNaN(parsed) ? null : parsed;
                          onNumberChange(`${pathPrefix}.secondaryFontSize`, numValue);
                        }}
                        onBlur={(e) => {
                          const value = e.target.value;
                          // Validate: field cannot be blank
                          if (value === '' || value === null || value === undefined) {
                            onNumberBlur(`${pathPrefix}.secondaryFontSize`, '');
                            return;
                          }
                          const parsed = parseInt(value);
                          // If NaN, keep empty to show validation error
                          const numValue = isNaN(parsed) ? null : parsed;
                          onNumberBlur(`${pathPrefix}.secondaryFontSize`, numValue);
                        }}
                        required
                        suffix="px"
                        helpText="Applies to: Progress message, Time left text, Reward description"
                        name={`${pathPrefix}_secondaryFontSize`}
                      />
                    </Box>
                    <Box minWidth="0" style={{ flex: 1 }}>
                      <MemoizedTextField
                        label="Button Font Size (px)"
                        type="number"
                        value={settings.buttonFontSize !== null && settings.buttonFontSize !== undefined && settings.buttonFontSize !== '' ? settings.buttonFontSize.toString() : ''}
                        onChange={(value) => {
                          // Allow empty string during typing
                          if (value === '' || value === null || value === undefined) {
                            onNumberChange(`${pathPrefix}.buttonFontSize`, '');
                            return;
                          }
                          const parsed = parseInt(value);
                          // Allow 0, 1, 2, etc. - only use default if NaN
                          const numValue = isNaN(parsed) ? null : parsed;
                          onNumberChange(`${pathPrefix}.buttonFontSize`, numValue);
                        }}
                        onBlur={(e) => {
                          const value = e.target.value;
                          // Validate: field cannot be blank
                          if (value === '' || value === null || value === undefined) {
                            onNumberBlur(`${pathPrefix}.buttonFontSize`, '');
                            return;
                          }
                          const parsed = parseInt(value);
                          // If NaN, keep empty to show validation error
                          const numValue = isNaN(parsed) ? null : parsed;
                          onNumberBlur(`${pathPrefix}.buttonFontSize`, numValue);
                        }}
                        required
                        suffix="px"
                        helpText="Applies to: See details button, Claim this perk button"
                        name={`${pathPrefix}_buttonFontSize`}
                      />
                    </Box>
                  </InlineStack>
                </BlockStack>
              </BlockStack>

              <BlockStack gap="400">
                <Box paddingBlockStart="300" paddingBlockEnd="300">
                  <div style={{ height: '1px', backgroundColor: '#e1e5e9', width: '100%' }} />
                </Box>
                <Text variant="headingMd" as="h2">Colors</Text>
                <InlineStack gap="300" wrap={false}>
                  <Box minWidth="0" style={{ flex: 1 }}>
                    <ColorPicker
                      label="Widget Background Color"
                      value={settings.backgroundColor}
                      onChange="backgroundColor"
                      pathPrefix={pathPrefix}
                  onColorChange={onColorChange}
                    />
                  </Box>
                  <Box minWidth="0" style={{ flex: 1 }}>
                    <ColorPicker
                      label="Header Color"
                      value={settings.headerColor}
                      onChange="headerColor"
                      pathPrefix={pathPrefix}
                  onColorChange={onColorChange}
                    />
                  </Box>
                </InlineStack>
                <InlineStack gap="300" wrap={false}>
                  <Box minWidth="0" style={{ flex: 1 }}>
                    <ColorPicker
                      label="Subtitle Color"
                      value={settings.subtitleColor}
                      onChange="subtitleColor"
                      pathPrefix={pathPrefix}
                  onColorChange={onColorChange}
                    />
                  </Box>
                  <Box minWidth="0" style={{ flex: 1 }}>
                    <ColorPicker
                      label="Text Color"
                      value={settings.textColor}
                      onChange="textColor"
                      pathPrefix={pathPrefix}
                  onColorChange={onColorChange}
                    />
                  </Box>
                </InlineStack>
                <InlineStack gap="300" wrap={false}>
                  <Box minWidth="0" style={{ flex: 1 }}>
                    <ColorPicker
                      label="Progress Bar Fill Color"
                      value={settings.progressBarColor}
                      onChange="progressBarColor"
                      pathPrefix={pathPrefix}
                  onColorChange={onColorChange}
                    />
                  </Box>
                  <Box minWidth="0" style={{ flex: 1 }}>
                    <ColorPicker
                      label="Progress Bar Background Color"
                      value={settings.progressBarBackgroundColor || defaults.progressBarBackgroundColor || '#e1e5e9'}
                      onChange="progressBarBackgroundColor"
                      pathPrefix={pathPrefix}
                  onColorChange={onColorChange}
                    />
                  </Box>
                </InlineStack>
                <InlineStack gap="300" wrap={false}>
                  <Box minWidth="0" style={{ flex: 1 }}>
                    <ColorPicker
                      label="Widget Border Color"
                      value={settings.widgetBorderColor || defaults.widgetBorderColor || '#e1e5e9'}
                      onChange="widgetBorderColor"
                      pathPrefix={pathPrefix}
                      onColorChange={onColorChange}
                    />
                  </Box>
                  <Box minWidth="0" style={{ flex: 1 }}>
                  </Box>
                </InlineStack>
              </BlockStack>
            </BlockStack>
          </Box>
        </Card>

        <Card>
          <Box>
            <BlockStack gap="400">
              <BlockStack gap="400">
                <Text variant="headingMd" as="h2">Reward Settings</Text>
                <BlockStack gap="400">
                  <InlineStack gap="300" wrap={false}>
                    <Box minWidth="0" style={{ flex: 1 }}>
                  <MemoizedTextField
                        label="See Details Button Text"
                        value={settings.rewardSeeDetailsButton || defaults.rewardSeeDetailsButton || ''}
                    onChange={(value) => onFieldChange(`${pathPrefix}.rewardSeeDetailsButton`, value)}
                        name={`${pathPrefix}_rewardSeeDetailsButton`}
                      />
                    </Box>
                    <Box minWidth="0" style={{ flex: 1 }}>
                  <MemoizedTextField
                        label="Claim Button Text"
                        value={settings.rewardClaimButton || defaults.rewardClaimButton || ''}
                onChange={(value) => onFieldChange(`${pathPrefix}.rewardClaimButton`, value)}
                        name={`${pathPrefix}_rewardClaimButton`}
                      />
                    </Box>
                  </InlineStack>
              <MemoizedTextField
                    label="Included Label"
                    value={settings.rewardIncludedLabel || defaults.rewardIncludedLabel || ''}
                onChange={(value) => onFieldChange(`${pathPrefix}.rewardIncludedLabel`, value)}
                    name={`${pathPrefix}_rewardIncludedLabel`}
                  />
                </BlockStack>
              </BlockStack>

              <BlockStack gap="400">
                <Box paddingBlockStart="300" paddingBlockEnd="300">
                  <div style={{ height: '1px', backgroundColor: '#e1e5e9', width: '100%' }} />
                </Box>
                <Text variant="headingMd" as="h2">Reward Colors</Text>
                <InlineStack gap="300" wrap={false}>
                  <Box minWidth="0" style={{ flex: 1 }}>
                    <ColorPicker
                      label="Card Background Color"
                      value={settings.rewardCardBackgroundColor || defaults.rewardCardBackgroundColor || '#ffffff'}
                      onChange="rewardCardBackgroundColor"
                      pathPrefix={pathPrefix}
                  onColorChange={onColorChange}
                    />
                  </Box>
                  <Box minWidth="0" style={{ flex: 1 }}>
                    <ColorPicker
                      label="Title Color"
                      value={settings.rewardTitleColor || defaults.rewardTitleColor || '#000000'}
                      onChange="rewardTitleColor"
                      pathPrefix={pathPrefix}
                  onColorChange={onColorChange}
                    />
                  </Box>
                </InlineStack>
                <InlineStack gap="300" wrap={false}>
                  <Box minWidth="0" style={{ flex: 1 }}>
                    <ColorPicker
                      label="Description Color"
                      value={settings.rewardDescriptionColor || defaults.rewardDescriptionColor || '#6b7280'}
                      onChange="rewardDescriptionColor"
                      pathPrefix={pathPrefix}
                  onColorChange={onColorChange}
                    />
                  </Box>
                  <Box minWidth="0" style={{ flex: 1 }}>
                    <ColorPicker
                      label="Price Color"
                      value={settings.rewardPriceColor || defaults.rewardPriceColor || '#000000'}
                      onChange="rewardPriceColor"
                      pathPrefix={pathPrefix}
                  onColorChange={onColorChange}
                    />
                  </Box>
                </InlineStack>
              </BlockStack>

              <BlockStack gap="400">
                <Box paddingBlockStart="300" paddingBlockEnd="300">
                  <div style={{ height: '1px', backgroundColor: '#e1e5e9', width: '100%' }} />
                </Box>
                <Text variant="headingMd" as="h2">Secondary Button Colors</Text>
                <InlineStack gap="300" wrap={false}>
                  <Box minWidth="0" style={{ flex: 1 }}>
                    <ColorPicker
                      label="Secondary Button Background Color"
                      value={settings.rewardSeeDetailsButtonBackgroundColor || defaults.rewardSeeDetailsButtonBackgroundColor || '#ffffff'}
                      onChange="rewardSeeDetailsButtonBackgroundColor"
                      pathPrefix={pathPrefix}
                  onColorChange={onColorChange}
                    />
                  </Box>
                  <Box minWidth="0" style={{ flex: 1 }}>
                    <ColorPicker
                      label="Secondary Button Text Color"
                      value={settings.rewardSeeDetailsButtonColor || defaults.rewardSeeDetailsButtonColor || '#000000'}
                      onChange="rewardSeeDetailsButtonColor"
                      pathPrefix={pathPrefix}
                  onColorChange={onColorChange}
                    />
                  </Box>
                </InlineStack>
              </BlockStack>

              <BlockStack gap="400">
                <Box paddingBlockStart="300" paddingBlockEnd="300">
                  <div style={{ height: '1px', backgroundColor: '#e1e5e9', width: '100%' }} />
                </Box>
                <Text variant="headingMd" as="h2">Primary Button Colors</Text>
                <InlineStack gap="300" wrap={false}>
                  <Box minWidth="0" style={{ flex: 1 }}>
                    <ColorPicker
                      label="Primary Button Background Color"
                      value={settings.rewardClaimButtonColor || defaults.rewardClaimButtonColor || '#000000'}
                      onChange="rewardClaimButtonColor"
                      pathPrefix={pathPrefix}
                  onColorChange={onColorChange}
                    />
                  </Box>
                  <Box minWidth="0" style={{ flex: 1 }}>
                    <ColorPicker
                      label="Primary Button Text Color"
                      value={settings.rewardClaimButtonTextColor || defaults.rewardClaimButtonTextColor || '#ffffff'}
                      onChange="rewardClaimButtonTextColor"
                      pathPrefix={pathPrefix}
                  onColorChange={onColorChange}
                    />
                  </Box>
                </InlineStack>
              </BlockStack>
            </BlockStack>
          </Box>
        </Card>
      </BlockStack>
  );
}, (prevProps, nextProps) => {
  // Custom comparison function for better memoization
  return JSON.stringify(prevProps.settings) === JSON.stringify(nextProps.settings) &&
    prevProps.pathPrefix === nextProps.pathPrefix;
});
CrowdfundingFormSection.displayName = 'CrowdfundingFormSection';

// Memoized Presales form section - simplified version (similar structure)
const PresalesFormSection = memo(({ settings, pathPrefix, onFieldChange, onColorChange, onNumberChange, onNumberBlur }) => {
  // Get default values from INITIAL_VALUES
  const defaults = INITIAL_VALUES.presales;
  // Similar structure to CrowdfundingFormSection but with presales-specific fields
    return (
      <BlockStack gap="500">
        <Card>
          <Box>
            <BlockStack gap="400">
              <Text variant="headingMd" as="h2">Text Content</Text>
              <BlockStack gap="400">
                <Text variant="headingMd" as="h3">When live text settings</Text>
                <InlineStack gap="300" wrap={false}>
                  <Box minWidth="0" style={{ flex: 1 }}>
                  <MemoizedTextField
                      label="When Live Title"
                      value={settings.whenLiveTitle}
                    onChange={(value) => onFieldChange(`${pathPrefix}.whenLiveTitle`, value)}
                      name={`${pathPrefix}_whenLiveTitle`}
                    />
                  </Box>
                  <Box minWidth="0" style={{ flex: 1 }}>
                  <MemoizedTextField
                      label="Claimed Percentage Text"
                      value={settings.claimedPercentageText || defaults.claimedPercentageText || ''}
                    onChange={(value) => onFieldChange(`${pathPrefix}.claimedPercentageText`, value)}
                      helpText="Use {percentage} placeholder"
                      name={`${pathPrefix}_claimedPercentageText`}
                    />
                  </Box>
                </InlineStack>
                <BlockStack gap="200">
                  <Text variant="bodyMd" fontWeight="medium" as="label" htmlFor={`${pathPrefix}_preOrderText`}>
                    Pre-order Text
                  </Text>
                  <textarea
                    id={`${pathPrefix}_preOrderText`}
                    name={`${pathPrefix}_preOrderText`}
                    value={settings.preOrderText || ''}
                    onChange={(e) => onFieldChange(`${pathPrefix}.preOrderText`, e.target.value)}
                    rows={4}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      fontSize: '14px',
                      fontFamily: 'inherit',
                      lineHeight: '1.5',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      resize: 'vertical',
                      boxSizing: 'border-box'
                    }}
                  />
                  <Text variant="bodySm" tone="subdued" as="p">
                    Use {'{goal}'} and {'{raised}'} placeholders
                  </Text>
                </BlockStack>
                
                  <MemoizedTextField
                      label="When Live Button"
                      value={settings.whenLiveButton}
                    onChange={(value) => onFieldChange(`${pathPrefix}.whenLiveButton`, value)}
                      name={`${pathPrefix}_whenLiveButton`}
                    />
              </BlockStack>

              <BlockStack gap="400">
                <Text variant="headingMd" as="h3">Sale ended text settings</Text>
                <InlineStack gap="300" wrap={false}>
                  <Box minWidth="0" style={{ flex: 1 }}>
                  <MemoizedTextField
                      label="After Live Title"
                      value={settings.afterLiveTitle}
                    onChange={(value) => onFieldChange(`${pathPrefix}.afterLiveTitle`, value)}
                      name={`${pathPrefix}_afterLiveTitle`}
                    />
                  </Box>
                  <Box minWidth="0" style={{ flex: 1 }}>
                  <MemoizedTextField
                      label="After Live Button"
                      value={settings.afterLiveButton}
                    onChange={(value) => onFieldChange(`${pathPrefix}.afterLiveButton`, value)}
                      name={`${pathPrefix}_afterLiveButton`}
                    />
                  </Box>
                </InlineStack>
              <MemoizedTextField
                  label="After Live Message"
                  value={settings.afterLiveMessage}
                onChange={(value) => onFieldChange(`${pathPrefix}.afterLiveMessage`, value)}
                  multiline={2}
                  name={`${pathPrefix}_afterLiveMessage`}
                />
              </BlockStack>

              <BlockStack gap="400">
                <Box paddingBlockStart="300" paddingBlockEnd="300">
                  <div style={{ height: '1px', backgroundColor: '#e1e5e9', width: '100%' }} />
                </Box>
                <Text variant="headingMd" as="h2">Layout & Style</Text>
                <InlineStack gap="300" wrap={false}>
                  <Box minWidth="0" style={{ flex: 1 }}>
                  <MemoizedTextField
                      label="Widget Border Radius"
                      type="number"
                      min="0"
                      value={settings.widgetBorderRadius !== null && settings.widgetBorderRadius !== undefined ? settings.widgetBorderRadius.toString() : ''}
                  onChange={(value) => onNumberChange(`${pathPrefix}.widgetBorderRadius`, value)}
                  onBlur={(e) => onNumberBlur(`${pathPrefix}.widgetBorderRadius`, e.target.value)}
                      onKeyUp={(e) => {
                        if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                        onNumberChange(`${pathPrefix}.widgetBorderRadius`, e.target.value);
                        }
                      }}
                      suffix="px"
                      name={`${pathPrefix}_widgetBorderRadius`}
                    />
                  </Box>
                </InlineStack>
              </BlockStack>

              <BlockStack gap="400">
                <Box paddingBlockStart="300" paddingBlockEnd="300">
                  <div style={{ height: '1px', backgroundColor: '#e1e5e9', width: '100%' }} />
                </Box>
                <Text variant="headingMd" as="h2">Typography</Text>
                <BlockStack gap="300">
                  <InlineStack gap="300" wrap={false}>
                    <Box minWidth="0" style={{ flex: 1 }}>
                      <MemoizedTextField
                        label="Header Font Size (px)"
                        type="number"
                        value={settings.headerFontSize !== null && settings.headerFontSize !== undefined && settings.headerFontSize !== '' ? settings.headerFontSize.toString() : ''}
                        onChange={(value) => {
                          // Allow empty string during typing
                          if (value === '' || value === null || value === undefined) {
                            onNumberChange(`${pathPrefix}.headerFontSize`, '');
                            return;
                          }
                          const parsed = parseInt(value);
                          // Allow 0, 1, 2, etc. - only use default if NaN
                          const numValue = isNaN(parsed) ? null : parsed;
                          onNumberChange(`${pathPrefix}.headerFontSize`, numValue);
                        }}
                        onBlur={(e) => {
                          const value = e.target.value;
                          // Validate: field cannot be blank
                          if (value === '' || value === null || value === undefined) {
                            onNumberBlur(`${pathPrefix}.headerFontSize`, '');
                            return;
                          }
                          const parsed = parseInt(value);
                          // If NaN, keep empty to show validation error
                          const numValue = isNaN(parsed) ? null : parsed;
                          onNumberBlur(`${pathPrefix}.headerFontSize`, numValue);
                        }}
                        required
                        suffix="px"
                        helpText="Applies to: When Live Title, After Live Title"
                        name={`${pathPrefix}_headerFontSize`}
                      />
                    </Box>
                    <Box minWidth="0" style={{ flex: 1 }}>
                      <MemoizedTextField
                        label="Primary Text Font Size (px)"
                        type="number"
                        value={settings.primaryFontSize !== null && settings.primaryFontSize !== undefined && settings.primaryFontSize !== '' ? settings.primaryFontSize.toString() : ''}
                        onChange={(value) => {
                          // Allow empty string during typing
                          if (value === '' || value === null || value === undefined) {
                            onNumberChange(`${pathPrefix}.primaryFontSize`, '');
                            return;
                          }
                          const parsed = parseInt(value);
                          // Allow 0, 1, 2, etc. - only use default if NaN
                          const numValue = isNaN(parsed) ? null : parsed;
                          onNumberChange(`${pathPrefix}.primaryFontSize`, numValue);
                        }}
                        onBlur={(e) => {
                          const value = e.target.value;
                          // Validate: field cannot be blank
                          if (value === '' || value === null || value === undefined) {
                            onNumberBlur(`${pathPrefix}.primaryFontSize`, '');
                            return;
                          }
                          const parsed = parseInt(value);
                          // If NaN, keep empty to show validation error
                          const numValue = isNaN(parsed) ? null : parsed;
                          onNumberBlur(`${pathPrefix}.primaryFontSize`, numValue);
                        }}
                        required
                        suffix="px"
                        helpText="Applies to: Pre-orders text, Claimed percentage, Backers count"
                        name={`${pathPrefix}_primaryFontSize`}
                      />
                    </Box>
                  </InlineStack>
                  <InlineStack gap="300" wrap={false}>
                    <Box minWidth="0" style={{ flex: 1 }}>
                      <MemoizedTextField
                        label="Secondary Text Font Size (px)"
                        type="number"
                        value={settings.secondaryFontSize !== null && settings.secondaryFontSize !== undefined && settings.secondaryFontSize !== '' ? settings.secondaryFontSize.toString() : ''}
                        onChange={(value) => {
                          // Allow empty string during typing
                          if (value === '' || value === null || value === undefined) {
                            onNumberChange(`${pathPrefix}.secondaryFontSize`, '');
                            return;
                          }
                          const parsed = parseInt(value);
                          // Allow 0, 1, 2, etc. - only use default if NaN
                          const numValue = isNaN(parsed) ? null : parsed;
                          onNumberChange(`${pathPrefix}.secondaryFontSize`, numValue);
                        }}
                        onBlur={(e) => {
                          const value = e.target.value;
                          // Validate: field cannot be blank
                          if (value === '' || value === null || value === undefined) {
                            onNumberBlur(`${pathPrefix}.secondaryFontSize`, '');
                            return;
                          }
                          const parsed = parseInt(value);
                          // If NaN, keep empty to show validation error
                          const numValue = isNaN(parsed) ? null : parsed;
                          onNumberBlur(`${pathPrefix}.secondaryFontSize`, numValue);
                        }}
                        required
                        suffix="px"
                        helpText="Applies to: Time left text, After Live message"
                        name={`${pathPrefix}_secondaryFontSize`}
                      />
                    </Box>
                    <Box minWidth="0" style={{ flex: 1 }}>
                      <MemoizedTextField
                        label="Button Font Size (px)"
                        type="number"
                        value={settings.buttonFontSize !== null && settings.buttonFontSize !== undefined && settings.buttonFontSize !== '' ? settings.buttonFontSize.toString() : ''}
                        onChange={(value) => {
                          // Allow empty string during typing
                          if (value === '' || value === null || value === undefined) {
                            onNumberChange(`${pathPrefix}.buttonFontSize`, '');
                            return;
                          }
                          const parsed = parseInt(value);
                          // Allow 0, 1, 2, etc. - only use default if NaN
                          const numValue = isNaN(parsed) ? null : parsed;
                          onNumberChange(`${pathPrefix}.buttonFontSize`, numValue);
                        }}
                        onBlur={(e) => {
                          const value = e.target.value;
                          // Validate: field cannot be blank
                          if (value === '' || value === null || value === undefined) {
                            onNumberBlur(`${pathPrefix}.buttonFontSize`, '');
                            return;
                          }
                          const parsed = parseInt(value);
                          // If NaN, keep empty to show validation error
                          const numValue = isNaN(parsed) ? null : parsed;
                          onNumberBlur(`${pathPrefix}.buttonFontSize`, numValue);
                        }}
                        required
                        suffix="px"
                        helpText="Applies to: Shop Pre-Sale button, Notify Me button"
                        name={`${pathPrefix}_buttonFontSize`}
                      />
                    </Box>
                  </InlineStack>
                </BlockStack>
              </BlockStack>

              <BlockStack gap="400">
                <Box paddingBlockStart="300" paddingBlockEnd="300">
                  <div style={{ height: '1px', backgroundColor: '#e1e5e9', width: '100%' }} />
                </Box>
                <Text variant="headingMd" as="h2">Colors</Text>
                <InlineStack gap="300" wrap={false}>
                  <Box minWidth="0" style={{ flex: 1 }}>
                    <ColorPicker
                      label="Widget Background Color"
                      value={settings.backgroundColor}
                      onChange="backgroundColor"
                      pathPrefix={pathPrefix}
                    onColorChange={onColorChange}
                    />
                  </Box>
                  <Box minWidth="0" style={{ flex: 1 }}>
                    <ColorPicker
                      label="Header Color"
                      value={settings.headerColor}
                      onChange="headerColor"
                      pathPrefix={pathPrefix}
                    onColorChange={onColorChange}
                    />
                  </Box>
                </InlineStack>
                <InlineStack gap="300" wrap={false}>
                  <Box minWidth="0" style={{ flex: 1 }}>
                    <ColorPicker
                      label="Subtitle Color"
                      value={settings.subtitleColor}
                      onChange="subtitleColor"
                      pathPrefix={pathPrefix}
                    onColorChange={onColorChange}
                    />
                  </Box>
                  <Box minWidth="0" style={{ flex: 1 }}>
                    <ColorPicker
                      label="Text Color"
                      value={settings.textColor}
                      onChange="textColor"
                      pathPrefix={pathPrefix}
                    onColorChange={onColorChange}
                    />
                  </Box>
                </InlineStack>
                <InlineStack gap="300" wrap={false}>
                  <Box minWidth="0" style={{ flex: 1 }}>
                    <ColorPicker
                      label="Progress Bar Fill Color"
                      value={settings.progressBarColor}
                      onChange="progressBarColor"
                      pathPrefix={pathPrefix}
                    onColorChange={onColorChange}
                    />
                  </Box>
                  <Box minWidth="0" style={{ flex: 1 }}>
                    <ColorPicker
                      label="Progress Bar Background Color"
                      value={settings.progressBarBackgroundColor || defaults.progressBarBackgroundColor || '#e1e5e9'}
                      onChange="progressBarBackgroundColor"
                      pathPrefix={pathPrefix}
                    onColorChange={onColorChange}
                    />
                  </Box>
                </InlineStack>
                <InlineStack gap="300" wrap={false}>
                  <Box minWidth="0" style={{ flex: 1 }}>
                    <ColorPicker
                      label="Widget Border Color"
                      value={settings.widgetBorderColor || defaults.widgetBorderColor || '#e1e5e9'}
                      onChange="widgetBorderColor"
                      pathPrefix={pathPrefix}
                      onColorChange={onColorChange}
                    />
                  </Box>
                  <Box minWidth="0" style={{ flex: 1 }}>
                    <ColorPicker
                      label="Sale ended Header Color"
                      value={settings.afterLiveTitleColor || defaults.afterLiveTitleColor || '#ef4444'}
                      onChange="afterLiveTitleColor"
                      pathPrefix={pathPrefix}
                      onColorChange={onColorChange}
                    />
                  </Box>
                </InlineStack>
              </BlockStack>

              <BlockStack gap="400">
                <Text variant="headingMd" as="h2">Button Colors</Text>
                <InlineStack gap="300" wrap={false}>
                  <Box minWidth="0" style={{ flex: 1 }}>
                    <ColorPicker
                      label="Button Background Color"
                      value={settings.buttonBackgroundColor || '#000000'}
                      onChange="buttonBackgroundColor"
                      pathPrefix={pathPrefix}
                      onColorChange={onColorChange}
                    />
                  </Box>
                  <Box minWidth="0" style={{ flex: 1 }}>
                    <ColorPicker
                      label="Button Text Color"
                      value={settings.buttonTextColor || '#ffffff'}
                      onChange="buttonTextColor"
                      pathPrefix={pathPrefix}
                      onColorChange={onColorChange}
                    />
                  </Box>
                </InlineStack>
              </BlockStack>
            </BlockStack>
          </Box>
        </Card>
      </BlockStack>
    );
}, (prevProps, nextProps) => {
  return JSON.stringify(prevProps.settings) === JSON.stringify(nextProps.settings) &&
    prevProps.pathPrefix === nextProps.pathPrefix;
});
PresalesFormSection.displayName = 'PresalesFormSection';

// Memoized Presales Form Section with Notify Me Modal
const PresalesFormSectionWithModal = memo(({ settings, pathPrefix, onFieldChange, onColorChange, onNumberChange, onNumberBlur }) => {
  return (
    <BlockStack gap="500">
      <PresalesFormSection 
        settings={settings} 
        pathPrefix={pathPrefix} 
        onFieldChange={onFieldChange} 
        onColorChange={onColorChange} 
        onNumberChange={onNumberChange} 
        onNumberBlur={onNumberBlur} 
      />
      <PresalesNotifyMeModalSection 
        settings={settings} 
        pathPrefix={pathPrefix} 
        onFieldChange={onFieldChange} 
        onColorChange={onColorChange} 
      />
    </BlockStack>
  );
}, (prevProps, nextProps) => {
  return JSON.stringify(prevProps.settings) === JSON.stringify(nextProps.settings) &&
    prevProps.pathPrefix === nextProps.pathPrefix;
});
PresalesFormSectionWithModal.displayName = 'PresalesFormSectionWithModal';

// Memoized Notify Me Modal Settings Section for Presales
const PresalesNotifyMeModalSection = memo(({ settings, pathPrefix, onFieldChange, onColorChange }) => {
  const defaults = INITIAL_VALUES.presales;
  
  return (
    <Card>
      <Box>
        <BlockStack gap="400">
          <Text variant="headingMd" as="h2">Notify Me Modal Settings</Text>
          <BlockStack gap="400">
            <MemoizedTextField
              label="Modal Title"
              value={settings.notifyMeModalTitle || defaults.notifyMeModalTitle || 'Notify Me'}
              onChange={(value) => onFieldChange(`${pathPrefix}.notifyMeModalTitle`, value)}
              name={`${pathPrefix}_notifyMeModalTitle`}
            />
            <MemoizedTextField
              label="Modal Description"
              value={settings.notifyMeModalDescription || defaults.notifyMeModalDescription || ''}
              onChange={(value) => onFieldChange(`${pathPrefix}.notifyMeModalDescription`, value)}
              multiline={2}
              name={`${pathPrefix}_notifyMeModalDescription`}
            />
            <MemoizedTextField
              label="Email Input Placeholder"
              value={settings.notifyMeEmailPlaceholder || defaults.notifyMeEmailPlaceholder || 'Enter your email'}
              onChange={(value) => onFieldChange(`${pathPrefix}.notifyMeEmailPlaceholder`, value)}
              name={`${pathPrefix}_notifyMeEmailPlaceholder`}
            />
            <MemoizedTextField
              label="Button Text"
              value={settings.notifyMeButtonText || defaults.notifyMeButtonText || 'Notify me'}
              onChange={(value) => onFieldChange(`${pathPrefix}.notifyMeButtonText`, value)}
              name={`${pathPrefix}_notifyMeButtonText`}
            />
          </BlockStack>

          <BlockStack gap="400">
            <Text variant="headingMd" as="h2">Modal Colors</Text>
            <Box paddingBlockEnd="300" borderBlockEnd="divider">
              <div style={{ height: '1px', backgroundColor: '#e1e5e9', width: '100%' }} />
            </Box>
            <InlineStack gap="300" wrap={false}>
              <Box minWidth="0" style={{ flex: 1 }}>
                <ColorPicker
                  label="Modal Background Color"
                  value={settings.notifyMeModalBackgroundColor || defaults.notifyMeModalBackgroundColor || '#ffffff'}
                  onChange="notifyMeModalBackgroundColor"
                  pathPrefix={pathPrefix}
                  onColorChange={onColorChange}
                />
              </Box>
              <Box minWidth="0" style={{ flex: 1 }}>
                <ColorPicker
                  label="Title Color"
                  value={settings.notifyMeModalTitleColor || defaults.notifyMeModalTitleColor || '#1a1a1a'}
                  onChange="notifyMeModalTitleColor"
                  pathPrefix={pathPrefix}
                  onColorChange={onColorChange}
                />
              </Box>
            </InlineStack>
            <InlineStack gap="300" wrap={false}>
              <Box minWidth="0" style={{ flex: 1 }}>
                <ColorPicker
                  label="Description Color"
                  value={settings.notifyMeModalDescriptionColor || defaults.notifyMeModalDescriptionColor || '#666666'}
                  onChange="notifyMeModalDescriptionColor"
                  pathPrefix={pathPrefix}
                  onColorChange={onColorChange}
                />
              </Box>
              <Box minWidth="0" style={{ flex: 1 }}>
                <ColorPicker
                  label="Button Background Color"
                  value={settings.notifyMeButtonBackgroundColor || defaults.notifyMeButtonBackgroundColor || '#4a90e2'}
                  onChange="notifyMeButtonBackgroundColor"
                  pathPrefix={pathPrefix}
                  onColorChange={onColorChange}
                />
              </Box>
            </InlineStack>
            <InlineStack gap="300" wrap={false}>
              <Box minWidth="0" style={{ flex: 1 }}>
                <ColorPicker
                  label="Button Text Color"
                  value={settings.notifyMeButtonTextColor || defaults.notifyMeButtonTextColor || '#ffffff'}
                  onChange="notifyMeButtonTextColor"
                  pathPrefix={pathPrefix}
                  onColorChange={onColorChange}
                />
              </Box>
              <Box minWidth="0" style={{ flex: 1 }}>
              </Box>
            </InlineStack>
          </BlockStack>
        </BlockStack>
      </Box>
    </Card>
  );
}, (prevProps, nextProps) => {
  return JSON.stringify(prevProps.settings) === JSON.stringify(nextProps.settings) &&
    prevProps.pathPrefix === nextProps.pathPrefix;
});
PresalesNotifyMeModalSection.displayName = 'PresalesNotifyMeModalSection';

// Memoized Product Drop form section
const ProductDropFormSection = memo(({ settings, pathPrefix, onFieldChange, onColorChange, onNumberChange, onNumberBlur }) => {
  // Get default values from INITIAL_VALUES
  const defaults = INITIAL_VALUES.productDrop;
  
    return (
      <BlockStack gap="500">
        <Card>
          <Box>
            <BlockStack gap="400">
              <Text variant="headingMd" as="h2">Text Content</Text>
              <BlockStack gap="400">
                <Text variant="headingMd" as="h3">Before live text settings</Text>
                <InlineStack gap="300" wrap={false}>
                  <Box minWidth="0" style={{ flex: 1 }}>
                  <MemoizedTextField
                      label="Before Live Title"
                      value={settings.beforeLiveTitle}
                    onChange={(value) => onFieldChange(`${pathPrefix}.beforeLiveTitle`, value)}
                      name={`${pathPrefix}_beforeLiveTitle`}
                    />
                  </Box>
                  <Box minWidth="0" style={{ flex: 1 }}>
                  <MemoizedTextField
                      label="Before Live Button"
                      value={settings.beforeLiveButton}
                    onChange={(value) => onFieldChange(`${pathPrefix}.beforeLiveButton`, value)}
                      name={`${pathPrefix}_beforeLiveButton`}
                    />
                  </Box>
                </InlineStack>
              <MemoizedTextField
                  label="Before Live Details"
                  value={settings.beforeLiveDetails}
                onChange={(value) => onFieldChange(`${pathPrefix}.beforeLiveDetails`, value)}
                  helpText="Use {days}, {hours}, {minutes} for the timer."
                  multiline={2}
                  name={`${pathPrefix}_beforeLiveDetails`}
                />
              </BlockStack>

              <BlockStack gap="400">
                <Text variant="headingMd" as="h3">When live text settings</Text>
                <InlineStack gap="300" wrap={false}>
                  <Box minWidth="0" style={{ flex: 1 }}>
                  <MemoizedTextField
                      label="When Live Title"
                      value={settings.whenLiveTitle}
                    onChange={(value) => onFieldChange(`${pathPrefix}.whenLiveTitle`, value)}
                      name={`${pathPrefix}_whenLiveTitle`}
                    />
                  </Box>
                  <Box minWidth="0" style={{ flex: 1 }}>
                  <MemoizedTextField
                      label="When Live Button"
                      value={settings.whenLiveButton}
                    onChange={(value) => onFieldChange(`${pathPrefix}.whenLiveButton`, value)}
                      name={`${pathPrefix}_whenLiveButton`}
                    />
                  </Box>
                </InlineStack>
                <InlineStack gap="300" wrap={false}>
                  <Box minWidth="0" style={{ flex: 1 }}>
                  <MemoizedTextField
                      label="Goal Text"
                      value={settings.goalText}
                    onChange={(value) => onFieldChange(`${pathPrefix}.goalText`, value)}
                      helpText="Use {goal} placeholder"
                      name={`${pathPrefix}_goalText`}
                    />
                  </Box>
                  <Box minWidth="0" style={{ flex: 1 }}>
                  <MemoizedTextField
                      label="Sold Text"
                      value={settings.raisedText}
                    onChange={(value) => onFieldChange(`${pathPrefix}.raisedText`, value)}
                      helpText="Use {raised} placeholder"
                      name={`${pathPrefix}_raisedText`}
                    />
                  </Box>
                </InlineStack>
                <MemoizedTextField
                    label="Claimed Percentage Text"
                    value={settings.claimedPercentageText || defaults.claimedPercentageText || ''}
                  onChange={(value) => onFieldChange(`${pathPrefix}.claimedPercentageText`, value)}
                    helpText="Use {percentage} placeholder"
                    name={`${pathPrefix}_claimedPercentageText`}
                  />
              <MemoizedTextField
                  label="Purchase Limit Text"
                  value={settings.whenLiveLimit}
                onChange={(value) => onFieldChange(`${pathPrefix}.whenLiveLimit`, value)}
                  helpText="Use {limit} placeholder"
                  multiline={2}
                  name={`${pathPrefix}_whenLiveLimit`}
                />
              </BlockStack>

              <BlockStack gap="400">
                <Text variant="headingMd" as="h3">Sale ended text settings</Text>
                <InlineStack gap="300" wrap={false}>
                  <Box minWidth="0" style={{ flex: 1 }}>
                  <MemoizedTextField
                      label="After Live Title"
                      value={settings.afterLiveTitle}
                    onChange={(value) => onFieldChange(`${pathPrefix}.afterLiveTitle`, value)}
                      name={`${pathPrefix}_afterLiveTitle`}
                    />
                  </Box>
                  <Box minWidth="0" style={{ flex: 1 }}>
                  <MemoizedTextField
                      label="After Live Button"
                      value={settings.afterLiveButton}
                    onChange={(value) => onFieldChange(`${pathPrefix}.afterLiveButton`, value)}
                      name={`${pathPrefix}_afterLiveButton`}
                    />
                  </Box>
                </InlineStack>
              <MemoizedTextField
                  label="After Live Message"
                  value={settings.afterLiveMessage}
                onChange={(value) => onFieldChange(`${pathPrefix}.afterLiveMessage`, value)}
                  multiline={2}
                  name={`${pathPrefix}_afterLiveMessage`}
                />
              </BlockStack>

              <BlockStack gap="400">
                <Box paddingBlockStart="300" paddingBlockEnd="300">
                  <div style={{ height: '1px', backgroundColor: '#e1e5e9', width: '100%' }} />
                </Box>
                <Text variant="headingMd" as="h2">Layout & Style</Text>
                <InlineStack gap="300" wrap={false}>
                  <Box minWidth="0" style={{ flex: 1 }}>
                  <MemoizedTextField
                      label="Widget Border Radius"
                      type="number"
                      min="0"
                      value={settings.widgetBorderRadius !== null && settings.widgetBorderRadius !== undefined ? settings.widgetBorderRadius.toString() : ''}
                  onChange={(value) => onNumberChange(`${pathPrefix}.widgetBorderRadius`, value)}
                  onBlur={(e) => onNumberBlur(`${pathPrefix}.widgetBorderRadius`, e.target.value)}
                      onKeyUp={(e) => {
                        if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                        onNumberChange(`${pathPrefix}.widgetBorderRadius`, e.target.value);
                        }
                      }}
                      suffix="px"
                      name={`${pathPrefix}_widgetBorderRadius`}
                    />
                  </Box>
                </InlineStack>
              </BlockStack>

              <BlockStack gap="400">
                <Box paddingBlockStart="300" paddingBlockEnd="300">
                  <div style={{ height: '1px', backgroundColor: '#e1e5e9', width: '100%' }} />
                </Box>
                <Text variant="headingMd" as="h2">Typography</Text>
                <BlockStack gap="300">
                  <InlineStack gap="300" wrap={false}>
                    <Box minWidth="0" style={{ flex: 1 }}>
                      <MemoizedTextField
                        label="Header Font Size (px)"
                        type="number"
                        value={settings.headerFontSize !== null && settings.headerFontSize !== undefined && settings.headerFontSize !== '' ? settings.headerFontSize.toString() : ''}
                        onChange={(value) => {
                          // Allow empty string during typing
                          if (value === '' || value === null || value === undefined) {
                            onNumberChange(`${pathPrefix}.headerFontSize`, '');
                            return;
                          }
                          const parsed = parseInt(value);
                          // Allow 0, 1, 2, etc. - only use default if NaN
                          const numValue = isNaN(parsed) ? null : parsed;
                          onNumberChange(`${pathPrefix}.headerFontSize`, numValue);
                        }}
                        onBlur={(e) => {
                          const value = e.target.value;
                          // Validate: field cannot be blank
                          if (value === '' || value === null || value === undefined) {
                            onNumberBlur(`${pathPrefix}.headerFontSize`, '');
                            return;
                          }
                          const parsed = parseInt(value);
                          // If NaN, keep empty to show validation error
                          const numValue = isNaN(parsed) ? null : parsed;
                          onNumberBlur(`${pathPrefix}.headerFontSize`, numValue);
                        }}
                        required
                        suffix="px"
                        helpText="Applies to: Before Live Title, When Live Title, After Live Title"
                        name={`${pathPrefix}_headerFontSize`}
                      />
                    </Box>
                    <Box minWidth="0" style={{ flex: 1 }}>
                      <MemoizedTextField
                        label="Primary Text Font Size (px)"
                        type="number"
                        value={settings.primaryFontSize !== null && settings.primaryFontSize !== undefined && settings.primaryFontSize !== '' ? settings.primaryFontSize.toString() : ''}
                        onChange={(value) => {
                          // Allow empty string during typing
                          if (value === '' || value === null || value === undefined) {
                            onNumberChange(`${pathPrefix}.primaryFontSize`, '');
                            return;
                          }
                          const parsed = parseInt(value);
                          // Allow 0, 1, 2, etc. - only use default if NaN
                          const numValue = isNaN(parsed) ? null : parsed;
                          onNumberChange(`${pathPrefix}.primaryFontSize`, numValue);
                        }}
                        onBlur={(e) => {
                          const value = e.target.value;
                          // Validate: field cannot be blank
                          if (value === '' || value === null || value === undefined) {
                            onNumberBlur(`${pathPrefix}.primaryFontSize`, '');
                            return;
                          }
                          const parsed = parseInt(value);
                          // If NaN, keep empty to show validation error
                          const numValue = isNaN(parsed) ? null : parsed;
                          onNumberBlur(`${pathPrefix}.primaryFontSize`, numValue);
                        }}
                        required
                        suffix="px"
                        helpText="Applies to: Sold text, Limited text, Claimed percentage"
                        name={`${pathPrefix}_primaryFontSize`}
                      />
                    </Box>
                  </InlineStack>
                  <InlineStack gap="300" wrap={false}>
                    <Box minWidth="0" style={{ flex: 1 }}>
                      <MemoizedTextField
                        label="Secondary Text Font Size (px)"
                        type="number"
                        value={settings.secondaryFontSize !== null && settings.secondaryFontSize !== undefined && settings.secondaryFontSize !== '' ? settings.secondaryFontSize.toString() : ''}
                        onChange={(value) => {
                          // Allow empty string during typing
                          if (value === '' || value === null || value === undefined) {
                            onNumberChange(`${pathPrefix}.secondaryFontSize`, '');
                            return;
                          }
                          const parsed = parseInt(value);
                          // Allow 0, 1, 2, etc. - only use default if NaN
                          const numValue = isNaN(parsed) ? null : parsed;
                          onNumberChange(`${pathPrefix}.secondaryFontSize`, numValue);
                        }}
                        onBlur={(e) => {
                          const value = e.target.value;
                          // Validate: field cannot be blank
                          if (value === '' || value === null || value === undefined) {
                            onNumberBlur(`${pathPrefix}.secondaryFontSize`, '');
                            return;
                          }
                          const parsed = parseInt(value);
                          // If NaN, keep empty to show validation error
                          const numValue = isNaN(parsed) ? null : parsed;
                          onNumberBlur(`${pathPrefix}.secondaryFontSize`, numValue);
                        }}
                        required
                        suffix="px"
                        helpText="Applies to: Time left text, After Live message, Drop details"
                        name={`${pathPrefix}_secondaryFontSize`}
                      />
                    </Box>
                    <Box minWidth="0" style={{ flex: 1 }}>
                      <MemoizedTextField
                        label="Button Font Size (px)"
                        type="number"
                        value={settings.buttonFontSize !== null && settings.buttonFontSize !== undefined && settings.buttonFontSize !== '' ? settings.buttonFontSize.toString() : ''}
                        onChange={(value) => {
                          // Allow empty string during typing
                          if (value === '' || value === null || value === undefined) {
                            onNumberChange(`${pathPrefix}.buttonFontSize`, '');
                            return;
                          }
                          const parsed = parseInt(value);
                          // Allow 0, 1, 2, etc. - only use default if NaN
                          const numValue = isNaN(parsed) ? null : parsed;
                          onNumberChange(`${pathPrefix}.buttonFontSize`, numValue);
                        }}
                        onBlur={(e) => {
                          const value = e.target.value;
                          // Validate: field cannot be blank
                          if (value === '' || value === null || value === undefined) {
                            onNumberBlur(`${pathPrefix}.buttonFontSize`, '');
                            return;
                          }
                          const parsed = parseInt(value);
                          // If NaN, keep empty to show validation error
                          const numValue = isNaN(parsed) ? null : parsed;
                          onNumberBlur(`${pathPrefix}.buttonFontSize`, numValue);
                        }}
                        required
                        suffix="px"
                        helpText="Applies to: Notify Me button, Buy Now button"
                        name={`${pathPrefix}_buttonFontSize`}
                      />
                    </Box>
                  </InlineStack>
                </BlockStack>
              </BlockStack>

              <BlockStack gap="400">
                <Box paddingBlockStart="300" paddingBlockEnd="300">
                  <div style={{ height: '1px', backgroundColor: '#e1e5e9', width: '100%' }} />
                </Box>
                <Text variant="headingMd" as="h2">Colors</Text>
                <InlineStack gap="300" wrap={false}>
                  <Box minWidth="0" style={{ flex: 1 }}>
                    <ColorPicker
                      label="Widget Background Color"
                      value={settings.backgroundColor}
                      onChange="backgroundColor"
                      pathPrefix={pathPrefix}
                    onColorChange={onColorChange}
                    />
                  </Box>
                  <Box minWidth="0" style={{ flex: 1 }}>
                    <ColorPicker
                      label="Header Color"
                      value={settings.headerColor}
                      onChange="headerColor"
                      pathPrefix={pathPrefix}
                    onColorChange={onColorChange}
                    />
                  </Box>
                </InlineStack>
                <InlineStack gap="300" wrap={false}>
                  <Box minWidth="0" style={{ flex: 1 }}>
                    <ColorPicker
                      label="Subtitle Color"
                      value={settings.subtitleColor}
                      onChange="subtitleColor"
                      pathPrefix={pathPrefix}
                    onColorChange={onColorChange}
                    />
                  </Box>
                  <Box minWidth="0" style={{ flex: 1 }}>
                    <ColorPicker
                      label="Text Color"
                      value={settings.textColor}
                      onChange="textColor"
                      pathPrefix={pathPrefix}
                    onColorChange={onColorChange}
                    />
                  </Box>
                </InlineStack>
                <InlineStack gap="300" wrap={false}>
                  <Box minWidth="0" style={{ flex: 1 }}>
                    <ColorPicker
                      label="Progress Bar Fill Color"
                      value={settings.progressBarColor}
                      onChange="progressBarColor"
                      pathPrefix={pathPrefix}
                    onColorChange={onColorChange}
                    />
                  </Box>
                  <Box minWidth="0" style={{ flex: 1 }}>
                    <ColorPicker
                      label="Progress Bar Background Color"
                      value={settings.progressBarBackgroundColor || defaults.progressBarBackgroundColor || '#e1e5e9'}
                      onChange="progressBarBackgroundColor"
                      pathPrefix={pathPrefix}
                    onColorChange={onColorChange}
                    />
                  </Box>
                </InlineStack>
                <InlineStack gap="300" wrap={false}>
                  <Box minWidth="0" style={{ flex: 1 }}>
                    <ColorPicker
                      label="Widget Border Color"
                      value={settings.widgetBorderColor || defaults.widgetBorderColor || '#e1e5e9'}
                      onChange="widgetBorderColor"
                      pathPrefix={pathPrefix}
                      onColorChange={onColorChange}
                    />
                  </Box>
                  <Box minWidth="0" style={{ flex: 1 }}>
                    <ColorPicker
                      label="Timer Text Color"
                      value={settings.timerTextColor || defaults.timerTextColor || '#000000'}
                      onChange="timerTextColor"
                      pathPrefix={pathPrefix}
                    onColorChange={onColorChange}
                    />
                  </Box>
                </InlineStack>
                <InlineStack gap="300" wrap={false}>
                  <Box minWidth="0" style={{ flex: 1 }}>
                    <ColorPicker
                      label="Sale ended Header Color"
                      value={settings.afterLiveTitleColor || defaults.afterLiveTitleColor || '#ef4444'}
                      onChange="afterLiveTitleColor"
                      pathPrefix={pathPrefix}
                      onColorChange={onColorChange}
                    />
                  </Box>
                  <Box minWidth="0" style={{ flex: 1 }}>
                  </Box>
                </InlineStack>
              </BlockStack>

              <BlockStack gap="400">
                <Text variant="headingMd" as="h2">Button Colors</Text>
                <InlineStack gap="300" wrap={false}>
                  <Box minWidth="0" style={{ flex: 1 }}>
                    <ColorPicker
                      label="Button Background Color"
                      value={settings.buttonBackgroundColor || '#000000'}
                      onChange="buttonBackgroundColor"
                      pathPrefix={pathPrefix}
                      onColorChange={onColorChange}
                    />
                  </Box>
                  <Box minWidth="0" style={{ flex: 1 }}>
                    <ColorPicker
                      label="Button Text Color"
                      value={settings.buttonTextColor || '#ffffff'}
                      onChange="buttonTextColor"
                      pathPrefix={pathPrefix}
                      onColorChange={onColorChange}
                    />
                  </Box>
                </InlineStack>
              </BlockStack>
            </BlockStack>
          </Box>
        </Card>
      </BlockStack>
    );
}, (prevProps, nextProps) => {
  return JSON.stringify(prevProps.settings) === JSON.stringify(nextProps.settings) &&
    prevProps.pathPrefix === nextProps.pathPrefix;
});
ProductDropFormSection.displayName = 'ProductDropFormSection';

// Memoized Product Drop Form Section with Notify Me Modal
const ProductDropFormSectionWithModal = memo(({ settings, pathPrefix, onFieldChange, onColorChange, onNumberChange, onNumberBlur }) => {
  return (
    <BlockStack gap="500">
      <ProductDropFormSection 
        settings={settings} 
        pathPrefix={pathPrefix} 
        onFieldChange={onFieldChange} 
        onColorChange={onColorChange} 
        onNumberChange={onNumberChange} 
        onNumberBlur={onNumberBlur} 
      />
      <ProductDropNotifyMeModalSection 
        settings={settings} 
        pathPrefix={pathPrefix} 
        onFieldChange={onFieldChange} 
        onColorChange={onColorChange} 
      />
    </BlockStack>
  );
}, (prevProps, nextProps) => {
  return JSON.stringify(prevProps.settings) === JSON.stringify(nextProps.settings) &&
    prevProps.pathPrefix === nextProps.pathPrefix;
});
ProductDropFormSectionWithModal.displayName = 'ProductDropFormSectionWithModal';

// Memoized Notify Me Modal Settings Section for Product Drop
const ProductDropNotifyMeModalSection = memo(({ settings, pathPrefix, onFieldChange, onColorChange }) => {
  const defaults = INITIAL_VALUES.productDrop;
  
  return (
    <Card>
      <Box>
        <BlockStack gap="400">
          <Text variant="headingMd" as="h2">Notify Me Modal Settings</Text>
          
          <BlockStack gap="400">
            <Text variant="headingMd" as="h3">Before Live Modal</Text>
            <BlockStack gap="400">
              <MemoizedTextField
                label="Modal Title"
                value={settings.notifyMeBeforeModalTitle || defaults.notifyMeBeforeModalTitle || 'Notify Me'}
                onChange={(value) => onFieldChange(`${pathPrefix}.notifyMeBeforeModalTitle`, value)}
                name={`${pathPrefix}_notifyMeBeforeModalTitle`}
              />
              <MemoizedTextField
                label="Modal Description"
                value={settings.notifyMeBeforeModalDescription || defaults.notifyMeBeforeModalDescription || ''}
                onChange={(value) => onFieldChange(`${pathPrefix}.notifyMeBeforeModalDescription`, value)}
                multiline={2}
                name={`${pathPrefix}_notifyMeBeforeModalDescription`}
              />
              <MemoizedTextField
                label="Email Input Placeholder"
                value={settings.notifyMeBeforeEmailPlaceholder || defaults.notifyMeBeforeEmailPlaceholder || 'Enter your email'}
                onChange={(value) => onFieldChange(`${pathPrefix}.notifyMeBeforeEmailPlaceholder`, value)}
                name={`${pathPrefix}_notifyMeBeforeEmailPlaceholder`}
              />
              <MemoizedTextField
                label="Button Text"
                value={settings.notifyMeBeforeButtonText || defaults.notifyMeBeforeButtonText || 'Notify me'}
                onChange={(value) => onFieldChange(`${pathPrefix}.notifyMeBeforeButtonText`, value)}
                name={`${pathPrefix}_notifyMeBeforeButtonText`}
              />
            </BlockStack>

            <BlockStack gap="400">
              <Box paddingBlockStart="300" paddingBlockEnd="300">
                <div style={{ height: '1px', backgroundColor: '#e1e5e9', width: '100%' }} />
              </Box>
              <Text variant="headingMd" as="h3">Before Live Modal Colors</Text>
              <InlineStack gap="300" wrap={false}>
                <Box minWidth="0" style={{ flex: 1 }}>
                  <ColorPicker
                    label="Modal Background Color"
                    value={settings.notifyMeBeforeModalBackgroundColor || defaults.notifyMeBeforeModalBackgroundColor || '#ffffff'}
                    onChange="notifyMeBeforeModalBackgroundColor"
                    pathPrefix={pathPrefix}
                    onColorChange={onColorChange}
                  />
                </Box>
                <Box minWidth="0" style={{ flex: 1 }}>
                  <ColorPicker
                    label="Title Color"
                    value={settings.notifyMeBeforeModalTitleColor || defaults.notifyMeBeforeModalTitleColor || '#1a1a1a'}
                    onChange="notifyMeBeforeModalTitleColor"
                    pathPrefix={pathPrefix}
                    onColorChange={onColorChange}
                  />
                </Box>
              </InlineStack>
              <InlineStack gap="300" wrap={false}>
                <Box minWidth="0" style={{ flex: 1 }}>
                  <ColorPicker
                    label="Description Color"
                    value={settings.notifyMeBeforeModalDescriptionColor || defaults.notifyMeBeforeModalDescriptionColor || '#666666'}
                    onChange="notifyMeBeforeModalDescriptionColor"
                    pathPrefix={pathPrefix}
                    onColorChange={onColorChange}
                  />
                </Box>
                <Box minWidth="0" style={{ flex: 1 }}>
                  <ColorPicker
                    label="Button Background Color"
                    value={settings.notifyMeBeforeButtonBackgroundColor || defaults.notifyMeBeforeButtonBackgroundColor || '#4a90e2'}
                    onChange="notifyMeBeforeButtonBackgroundColor"
                    pathPrefix={pathPrefix}
                    onColorChange={onColorChange}
                  />
                </Box>
              </InlineStack>
              <InlineStack gap="300" wrap={false}>
                <Box minWidth="0" style={{ flex: 1 }}>
                  <ColorPicker
                    label="Button Text Color"
                    value={settings.notifyMeBeforeButtonTextColor || defaults.notifyMeBeforeButtonTextColor || '#ffffff'}
                    onChange="notifyMeBeforeButtonTextColor"
                    pathPrefix={pathPrefix}
                    onColorChange={onColorChange}
                  />
                </Box>
                <Box minWidth="0" style={{ flex: 1 }}>
                </Box>
              </InlineStack>
            </BlockStack>
          </BlockStack>

          <BlockStack gap="400">
            <Text variant="headingMd" as="h3">After Live Modal (Restock)</Text>
            <BlockStack gap="400">
              <MemoizedTextField
                label="Modal Title"
                value={settings.notifyMeAfterModalTitle || defaults.notifyMeAfterModalTitle || 'Notify Me'}
                onChange={(value) => onFieldChange(`${pathPrefix}.notifyMeAfterModalTitle`, value)}
                name={`${pathPrefix}_notifyMeAfterModalTitle`}
              />
              <MemoizedTextField
                label="Modal Description"
                value={settings.notifyMeAfterModalDescription || defaults.notifyMeAfterModalDescription || ''}
                onChange={(value) => onFieldChange(`${pathPrefix}.notifyMeAfterModalDescription`, value)}
                multiline={2}
                name={`${pathPrefix}_notifyMeAfterModalDescription`}
              />
              <MemoizedTextField
                label="Email Input Placeholder"
                value={settings.notifyMeAfterEmailPlaceholder || defaults.notifyMeAfterEmailPlaceholder || 'Enter your email'}
                onChange={(value) => onFieldChange(`${pathPrefix}.notifyMeAfterEmailPlaceholder`, value)}
                name={`${pathPrefix}_notifyMeAfterEmailPlaceholder`}
              />
              <MemoizedTextField
                label="Button Text"
                value={settings.notifyMeAfterButtonText || defaults.notifyMeAfterButtonText || 'Notify me'}
                onChange={(value) => onFieldChange(`${pathPrefix}.notifyMeAfterButtonText`, value)}
                name={`${pathPrefix}_notifyMeAfterButtonText`}
              />
            </BlockStack>

            <BlockStack gap="400">
              <Box paddingBlockStart="300" paddingBlockEnd="300">
                <div style={{ height: '1px', backgroundColor: '#e1e5e9', width: '100%' }} />
              </Box>
              <Text variant="headingMd" as="h3">After Live Modal Colors</Text>
              <InlineStack gap="300" wrap={false}>
                <Box minWidth="0" style={{ flex: 1 }}>
                  <ColorPicker
                    label="Modal Background Color"
                    value={settings.notifyMeAfterModalBackgroundColor || defaults.notifyMeAfterModalBackgroundColor || '#ffffff'}
                    onChange="notifyMeAfterModalBackgroundColor"
                    pathPrefix={pathPrefix}
                    onColorChange={onColorChange}
                  />
                </Box>
                <Box minWidth="0" style={{ flex: 1 }}>
                  <ColorPicker
                    label="Title Color"
                    value={settings.notifyMeAfterModalTitleColor || defaults.notifyMeAfterModalTitleColor || '#1a1a1a'}
                    onChange="notifyMeAfterModalTitleColor"
                    pathPrefix={pathPrefix}
                    onColorChange={onColorChange}
                  />
                </Box>
              </InlineStack>
              <InlineStack gap="300" wrap={false}>
                <Box minWidth="0" style={{ flex: 1 }}>
                  <ColorPicker
                    label="Description Color"
                    value={settings.notifyMeAfterModalDescriptionColor || defaults.notifyMeAfterModalDescriptionColor || '#666666'}
                    onChange="notifyMeAfterModalDescriptionColor"
                    pathPrefix={pathPrefix}
                    onColorChange={onColorChange}
                  />
                </Box>
                <Box minWidth="0" style={{ flex: 1 }}>
                  <ColorPicker
                    label="Button Background Color"
                    value={settings.notifyMeAfterButtonBackgroundColor || defaults.notifyMeAfterButtonBackgroundColor || '#4a90e2'}
                    onChange="notifyMeAfterButtonBackgroundColor"
                    pathPrefix={pathPrefix}
                    onColorChange={onColorChange}
                  />
                </Box>
              </InlineStack>
              <InlineStack gap="300" wrap={false}>
                <Box minWidth="0" style={{ flex: 1 }}>
                  <ColorPicker
                    label="Button Text Color"
                    value={settings.notifyMeAfterButtonTextColor || defaults.notifyMeAfterButtonTextColor || '#ffffff'}
                    onChange="notifyMeAfterButtonTextColor"
                    pathPrefix={pathPrefix}
                    onColorChange={onColorChange}
                  />
                </Box>
                <Box minWidth="0" style={{ flex: 1 }}>
                </Box>
              </InlineStack>
            </BlockStack>
          </BlockStack>
        </BlockStack>
      </Box>
    </Card>
  );
}, (prevProps, nextProps) => {
  return JSON.stringify(prevProps.settings) === JSON.stringify(nextProps.settings) &&
    prevProps.pathPrefix === nextProps.pathPrefix;
});
ProductDropNotifyMeModalSection.displayName = 'ProductDropNotifyMeModalSection';

// Image Placeholder Component - matches the skeleton image design
const ImagePlaceholder = memo(({ size = 'medium' }) => {
  const sizeMap = {
    small: { width: '32px', height: '32px', iconSize: 16 },
    medium: { width: '64px', height: '64px', iconSize: 32 },
    large: { width: '120px', height: '120px', iconSize: 60 }
  };
  
  const dimensions = sizeMap[size] || sizeMap.medium;
  
  return (
    <div style={{
      width: dimensions.width,
      height: dimensions.height,
      backgroundColor: '#f6f6f7',
      border: '1px solid #d1d5db',
      borderRadius: '8px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'relative'
    }}>
      <svg 
        width={dimensions.iconSize} 
        height={dimensions.iconSize} 
        viewBox="0 0 24 24" 
        fill="none" 
        xmlns="http://www.w3.org/2000/svg"
        style={{ opacity: 0.6 }}
      >
        <rect x="2" y="2" width="20" height="20" rx="2" stroke="#6b7280" strokeWidth="1.5" fill="none"/>
        <circle cx="18" cy="6" r="2.5" fill="#6b7280" fillOpacity="0.7"/>
        <path d="M4 20L8 14L12 18L20 10V20H4Z" fill="#6b7280" fillOpacity="0.4"/>
        <path d="M4 20L6 16L10 20H4Z" fill="#6b7280" fillOpacity="0.5"/>
      </svg>
    </div>
  );
});
ImagePlaceholder.displayName = 'ImagePlaceholder';

// Memoized Preview component - separated for better performance
const PreviewComponent = memo(({ activeTab, settings }) => {
  // Get install data from Redux (using reusable hook - ensures install API is only called once)
  const { installData } = useEnsureInstall();
  
  // Get default values from INITIAL_VALUES
  const crowdfundingDefaults = INITIAL_VALUES.crowdfunding;
  const presalesDefaults = INITIAL_VALUES.presales;
  const productDropDefaults = INITIAL_VALUES.productDrop;
  
  const decodeForPreview = useCallback((text) => {
    if (typeof text !== 'string') return text;
    return decodeHtmlEntities(text);
  }, []);

  const getContrastColor = useCallback((hexColor) => {
    let hex = hexColor.replace('#', '');
      if (hex.length === 3) {
        hex = hex.split('').map(char => char + char).join('');
      }
      if (!/^[0-9A-Fa-f]{6}$/.test(hex)) {
      return '#000000';
      }
      const r = parseInt(hex.substr(0, 2), 16);
      const g = parseInt(hex.substr(2, 2), 16);
      const b = parseInt(hex.substr(4, 2), 16);
      const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
      return luminance > 0.5 ? '#000000' : '#ffffff';
  }, []);

  // Get money format from install data
  const moneyFormat = useMemo(() => {
    return installData?.shop_info?.money_format || '${{amount}}';
  }, [installData]);

  const formatCurrency = useCallback((amount, decimals = 0) => {
    return formatMoney(amount, moneyFormat, { decimals });
  }, [moneyFormat]);

  const currentSettings = useMemo(() => {
    const baseSettings = (() => {
      switch (activeTab) {
        case 1: return settings.crowdfunding;
        case 2: return settings.presales;
        case 3: return settings.productDrop;
        default: return settings.crowdfunding;
      }
    })();
    
    // Merge with defaults based on active tab
    if (activeTab === 1) {
      // Crowdfunding with reward settings - merge all fields with defaults
      return {
        ...crowdfundingDefaults,
        ...baseSettings,
        // Ensure all fields are present
        fundedText: baseSettings.fundedText || crowdfundingDefaults.fundedText,
        progressBarBackgroundColor: baseSettings.progressBarBackgroundColor || crowdfundingDefaults.progressBarBackgroundColor,
      };
    } else if (activeTab === 2) {
      // Presales with defaults - merge all fields with defaults
      return {
        ...presalesDefaults,
        ...baseSettings,
        // Ensure all fields are present
        progressBarBackgroundColor: baseSettings.progressBarBackgroundColor || presalesDefaults.progressBarBackgroundColor,
      };
    } else if (activeTab === 3) {
      // Product Drop with defaults - merge all fields with defaults
      return {
        ...productDropDefaults,
        ...baseSettings,
        // Ensure all fields are present
        progressBarBackgroundColor: baseSettings.progressBarBackgroundColor || productDropDefaults.progressBarBackgroundColor,
      };
    }
    
    return baseSettings;
  }, [activeTab, settings, crowdfundingDefaults, presalesDefaults, productDropDefaults]);

  // Memoize progress data
  const progressData = useMemo(() => {
    const raised = 32500;
    const goal = 50000;
    const progress = (raised / goal) * 100;
    return {
      progress: Math.round(progress * 100) / 100,
      raised,
      goal
    };
  }, []);

  const calculateProgress = useCallback(() => 'XX', []);
  
  // Get current time in readable format
  const getCurrentTime = useCallback(() => {
    const now = new Date();
    const hours = now.getHours();
    const minutes = now.getMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    const displayMinutes = minutes.toString().padStart(2, '0');
    return `${displayHours}:${displayMinutes} ${ampm}`;
  }, []);
  
  const calculateTimeRemaining = useCallback(() => {
    // Calculate sample time remaining: 2 days, 5 hours, 30 minutes
    const days = 2;
    const hours = 5;
    const minutes = 30;
    return { days, hours, minutes };
  }, []);
  
  const formatTimeRemaining = useCallback((text) => {
    if (!text) return '';
    const timeRemaining = calculateTimeRemaining();

    const DAY_MARKER = '__LIFT_DAY__';
    const HOUR_MARKER = '__LIFT_HOUR__';
    const MINUTE_MARKER = '__LIFT_MINUTE__';

    let replacedText = text
      .replace(/{days}|{day}/gi, DAY_MARKER)
      .replace(/{hours}|{hour}/gi, HOUR_MARKER)
      .replace(/{minutes}|{minute}/gi, MINUTE_MARKER);

    const pluralizeTokenUnit = (inputText, marker, unit, value) => {
      return inputText
        .replace(new RegExp(`${marker}\\s+(${unit}s?)\\b`, 'gi'), (match, rawUnit) => {
          const singularUnit = rawUnit.replace(/s$/i, '');
          const pluralSuffix = /^[A-Z]+$/.test(singularUnit) ? 'S' : 's';
          return `${value} ${singularUnit}${value <= 1 ? '' : pluralSuffix}`;
        })
        .replace(new RegExp(marker, 'g'), value.toString());
    };

    replacedText = pluralizeTokenUnit(replacedText, DAY_MARKER, 'day', timeRemaining.days);
    replacedText = pluralizeTokenUnit(replacedText, HOUR_MARKER, 'hour', timeRemaining.hours);
    replacedText = pluralizeTokenUnit(replacedText, MINUTE_MARKER, 'minute', timeRemaining.minutes);

    return replacedText;
  }, [calculateTimeRemaining]);

  // Render preview based on active tab - memoized sections
    if (activeTab === 1) {
      return (
        <>
        <Card>
          <Box padding="400">
            <div className="preview-container">
              <div className="preview-header">
                <h3>Crowdfunding Widget Preview</h3>
              </div>
              <div className="preview-widget" style={{
            fontSize: '13px',
            backgroundColor: currentSettings.backgroundColor,
            padding: '16px',
            borderRadius: `${currentSettings.widgetBorderRadius}px`,
            color: currentSettings.textColor,
            border: `1px solid ${currentSettings.widgetBorderColor || crowdfundingDefaults.widgetBorderColor || '#e1e5e9'}`
              }}>
            {/* Campaign Title */}
            <h3 style={{
              color: currentSettings.headerColor || crowdfundingDefaults.headerColor,
              fontSize: `${currentSettings.headerFontSize || crowdfundingDefaults.headerFontSize || 16}px`,
              fontWeight: 'bold',
              margin: '0 0 16px 0'
            }}>
              Demo Funding
            </h3>
            <div >
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
                <span style={{ fontWeight: '600', fontSize: `${currentSettings.primaryFontSize || crowdfundingDefaults.primaryFontSize || 14}px` }}>
                      {decodeForPreview(currentSettings.raisedText).replace('{raised}', 'XX.XX')}
                </span>
                <span style={{ fontWeight: '600', fontSize: `${currentSettings.primaryFontSize || crowdfundingDefaults.primaryFontSize || 14}px` }}>
                      {decodeForPreview(currentSettings.goalText).replace('{goal}', 'XX.XX')}
                </span>
              </div>
              <div style={{
                width: '100%',
                height: '8px',
                backgroundColor: currentSettings.progressBarBackgroundColor || crowdfundingDefaults.progressBarBackgroundColor || '#e1e5e9',
                borderRadius: '4px',
                overflow: 'hidden',
                marginBottom: '12px'
              }}>
                <div style={{
                  width: '50%',
                  height: '100%',
                  backgroundColor: currentSettings.progressBarColor || crowdfundingDefaults.progressBarColor,
                  transition: 'width 0.3s ease'
                }} />
              </div>
              <p style={{ 
                color: currentSettings.subtitleColor,
                fontSize: `${currentSettings.secondaryFontSize || crowdfundingDefaults.secondaryFontSize || 13}px`,
                margin: '0 0 12px 0'
              }}>
                    {decodeForPreview(currentSettings.progressMessage)}
              </p>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
                <span style={{ fontWeight: '600', fontSize: `${currentSettings.primaryFontSize || crowdfundingDefaults.primaryFontSize || 14}px` }}>
                  {decodeForPreview(currentSettings.fundedText || crowdfundingDefaults.fundedText || '{percentage}% funded').replace('{percentage}', 'XX')}
                </span>
                <span style={{ fontWeight: '600', fontSize: `${currentSettings.primaryFontSize || crowdfundingDefaults.primaryFontSize || 14}px` }}>
                      {decodeForPreview(currentSettings.backerText).replace('{backers}', 'XX')}
                </span>
              </div>
              <div>
                <span style={{ 
                  color: currentSettings.subtitleColor,
                  fontSize: `${currentSettings.secondaryFontSize || crowdfundingDefaults.secondaryFontSize || 13}px`
                }}>
                      {formatTimeRemaining(decodeForPreview(currentSettings.timeLeftText))}
                </span>
              </div>
            </div>
          </div>
        </div>
          </Box>
        </Card>
        <Card>
          <Box padding="400">
            <div className="preview-container">
              <div className="preview-header">
                <h3>Reward Preview</h3>
              </div>
              <div style={{
                backgroundColor: currentSettings.rewardCardBackgroundColor || crowdfundingDefaults.rewardCardBackgroundColor || '#ffffff',
                border: `1px solid ${currentSettings.widgetBorderColor || crowdfundingDefaults.widgetBorderColor || '#e1e5e9'}`,
                borderRadius: `${currentSettings.widgetBorderRadius}px`,
                padding: '16px',
                marginTop: '16px',
                marginBottom: '16px'
              }}>
                <div style={{ textAlign: 'center', marginBottom: '12px' }}>
                  <div style={{
                    margin: '0 auto',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <ImagePlaceholder size="large" />
                  </div>
                </div>
                <h4 style={{ 
                  color: currentSettings.rewardTitleColor || crowdfundingDefaults.rewardTitleColor || '#000000',
                  fontSize: `${currentSettings.headerFontSize || crowdfundingDefaults.headerFontSize || 16}px`,
                  fontWeight: 'bold',
                  margin: '0 0 8px 0'
                }}>
                  Reward Title
                </h4>
                <p style={{ 
                  color: currentSettings.rewardPriceColor || crowdfundingDefaults.rewardPriceColor || '#000000',
                  fontSize: `${currentSettings.primaryFontSize || crowdfundingDefaults.primaryFontSize || 14}px`,
                  fontWeight: 'bold',
                  margin: '0 0 8px 0'
                }}>
                  XX.XX
                </p>
                <p style={{ 
                  color: currentSettings.rewardDescriptionColor || crowdfundingDefaults.rewardDescriptionColor || '#6b7280',
                  fontSize: `${currentSettings.secondaryFontSize || crowdfundingDefaults.secondaryFontSize || 13}px`,
                  margin: '0 0 12px 0',
                  lineHeight: '1.5'
                }}>
                  Reward Description
                </p>
                <div style={{ marginBottom: '12px' }}>
                  <p style={{ 
                    fontSize: '12px',
                    fontWeight: '600',
                    margin: '0 0 8px 0',
                    color: currentSettings.textColor
                  }}>
                    {decodeForPreview(currentSettings.rewardIncludedLabel || crowdfundingDefaults.rewardIncludedLabel || 'Included')}
                  </p>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    {[1, 2, 3].map(i => (
                      <ImagePlaceholder key={i} size="small" />
                    ))}
                  </div>
                    </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button 
                    onClick={(e) => e.preventDefault()}
                    style={{
                      backgroundColor: currentSettings.rewardSeeDetailsButtonBackgroundColor || crowdfundingDefaults.rewardSeeDetailsButtonBackgroundColor || '#ffffff',
                      color: currentSettings.rewardSeeDetailsButtonColor || crowdfundingDefaults.rewardSeeDetailsButtonColor || '#000000',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '6px 12px',
                      fontSize: `${currentSettings.buttonFontSize || crowdfundingDefaults.buttonFontSize || 14}px`,
                      fontWeight: '600',
                      cursor: 'default',
                      flex: 1,
                      transition: 'all 0.2s ease'
                    }}>
                    {decodeForPreview(currentSettings.rewardSeeDetailsButton || crowdfundingDefaults.rewardSeeDetailsButton || 'See details')}
                  </button>
                  <button 
                    onClick={(e) => e.preventDefault()}
                    style={{
                      backgroundColor: currentSettings.rewardClaimButtonColor || crowdfundingDefaults.rewardClaimButtonColor || '#000000',
                      color: currentSettings.rewardClaimButtonTextColor || crowdfundingDefaults.rewardClaimButtonTextColor || '#ffffff',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '6px 12px',
                      fontSize: `${currentSettings.buttonFontSize || crowdfundingDefaults.buttonFontSize || 14}px`,
                      fontWeight: '600',
                      cursor: 'default',
                      flex: 1,
                      transition: 'all 0.2s ease'
                    }}>
                    {decodeForPreview(currentSettings.rewardClaimButton || crowdfundingDefaults.rewardClaimButton || 'Claim this perk')}
                  </button>
                </div>
              </div>
            </div>
          </Box>
        </Card>
        </>
      );
    }

  // Presales preview
  if (activeTab === 2) {
    return (
      <>
        <Card>
          <Box padding="400">
            <div className="preview-container">
              <div className="preview-header">
                <h3>Presales Widget Preview</h3>
              </div>
              <div className="preview-widget" style={{
                fontSize: '13px',
                padding: '16px',
                borderRadius: '8px'
              }}>

                {/* Pre-Sale Is Live State */}
                <p style={{
                    fontSize: '12px',
                    fontWeight: '600',
                    margin: '0 0 12px 0',
                  }}>
                    When live
                  </p>
                <div style={{
                  backgroundColor: currentSettings.backgroundColor || presalesDefaults.backgroundColor,
                  border: `1px solid ${currentSettings.widgetBorderColor || presalesDefaults.widgetBorderColor || '#e1e5e9'}`,
                  borderRadius: `${currentSettings.widgetBorderRadius || presalesDefaults.widgetBorderRadius}px`,
                  padding: '16px',
                  marginBottom: '16px'
                }}>

                  <h4 style={{
                    fontSize: `${currentSettings.headerFontSize || presalesDefaults.headerFontSize || 16}px`,
                    fontWeight: 'bold',
                    margin: '0 0 16px 0',
                    color: currentSettings.headerColor || presalesDefaults.headerColor
                  }}>
                    {decodeForPreview(currentSettings.whenLiveTitle || presalesDefaults.whenLiveTitle)}
                  </h4>

                  {/* Progress Bar */}
                  <div style={{ marginBottom: '16px' }}>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '8px' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{
                          width: '100%',
                          height: '8px',
                          backgroundColor: currentSettings.progressBarBackgroundColor || presalesDefaults.progressBarBackgroundColor || '#e1e5e9',
                          borderRadius: '4px',
                          overflow: 'hidden'
                        }}>
                          <div style={{
                            width: '50%',
                            height: '100%',
                            backgroundColor: currentSettings.progressBarColor || presalesDefaults.progressBarColor,
                            transition: 'width 0.3s ease'
                          }} />
                        </div>
                      </div>
                      <span style={{ fontWeight: '600', fontSize: `${currentSettings.primaryFontSize || presalesDefaults.primaryFontSize || 14}px`, color: currentSettings.textColor || presalesDefaults.textColor }}>
                        {decodeForPreview(currentSettings.claimedPercentageText || presalesDefaults.claimedPercentageText || '{percentage}% Claimed').replace('{percentage}', 'XX')}
                      </span>
                    </div>
                    <p style={{
                      margin: 0,
                      color: currentSettings.subtitleColor || presalesDefaults.subtitleColor,
                      fontSize: `${currentSettings.primaryFontSize || presalesDefaults.primaryFontSize || 14}px`
                    }}>
                      {decodeForPreview(currentSettings.preOrderText || presalesDefaults.preOrderText || '').replace('{raised}', 'XX.XX').replace('{goal}', 'XX.XX')}
                    </p>
                  </div>

                  {/* Shop Pre-Sale Button */}
                  <button 
                    onClick={(e) => e.preventDefault()}
                    style={{
                      border: `2px solid ${currentSettings.buttonBackgroundColor || presalesDefaults.buttonBackgroundColor || '#000000'}`,
                      backgroundColor: currentSettings.buttonBackgroundColor || presalesDefaults.buttonBackgroundColor || '#000000',
                      color: currentSettings.buttonTextColor || presalesDefaults.buttonTextColor || '#ffffff',
                      borderRadius: currentSettings.buttonStyle === 'pill' ? '50px' :
                        currentSettings.buttonStyle === 'square' ? '0px' :
                          '8px',
                      padding: '5px 16px',
                      fontSize: `${currentSettings.buttonFontSize || presalesDefaults.buttonFontSize || 14}px`,
                      fontWeight: '600',
                      cursor: 'default',
                      width: '100%',
                    }}>
                    {decodeForPreview(currentSettings.whenLiveButton || presalesDefaults.whenLiveButton)}
                  </button>
                </div>

                <p style={{
                    fontSize: '12px',
                    fontWeight: '600',
                    margin: '0 0 12px 0'
                  }}>
                    Sale ended
                  </p>

                {/* Pre-Sale Has Ended State */}
                <div style={{
                  backgroundColor: currentSettings.backgroundColor || presalesDefaults.backgroundColor,
                  borderRadius: `${currentSettings.widgetBorderRadius || presalesDefaults.widgetBorderRadius}px`,
                  padding: '16px',
                  marginBottom: '16px',
                  border: `1px solid ${currentSettings.widgetBorderColor || presalesDefaults.widgetBorderColor || '#e1e5e9'}`
                }}>
                  

                  <h4 style={{
                    fontSize: `${currentSettings.headerFontSize || presalesDefaults.headerFontSize || 16}px`,
                    fontWeight: 'bold',
                    margin: '0 0 12px 0',
                    color: currentSettings.afterLiveTitleColor || presalesDefaults.afterLiveTitleColor || '#ef4444'
                  }}>
                    {decodeForPreview(currentSettings.afterLiveTitle || presalesDefaults.afterLiveTitle)}
                  </h4>

                  <p style={{
                    margin: '0 0 12px 0',
                    color: currentSettings.textColor || presalesDefaults.textColor,
                    fontSize: `${currentSettings.secondaryFontSize || presalesDefaults.secondaryFontSize || 13}px`
                  }}>
                    {decodeForPreview(currentSettings.afterLiveMessage || presalesDefaults.afterLiveMessage)}
                  </p>

                  {/* Notify for Restock Button */}
                  <button 
                    onClick={(e) => e.preventDefault()}
                    style={{
                      border: `2px solid ${currentSettings.buttonBackgroundColor || presalesDefaults.buttonBackgroundColor || '#000000'}`,
                      backgroundColor: currentSettings.buttonBackgroundColor || presalesDefaults.buttonBackgroundColor || '#000000',
                      color: currentSettings.buttonTextColor || presalesDefaults.buttonTextColor || '#ffffff',
                      borderRadius: currentSettings.buttonStyle === 'pill' ? '50px' :
                        currentSettings.buttonStyle === 'square' ? '0px' :
                          '8px',
                      padding: '5px 16px',
                      fontSize: `${currentSettings.buttonFontSize || presalesDefaults.buttonFontSize || 14}px`,
                      fontWeight: '600',
                      cursor: 'default',
                      width: '100%',
                    }}>
                    {decodeForPreview(currentSettings.afterLiveButton || presalesDefaults.afterLiveButton)}
                  </button>
                </div>
              </div>
            </div>
          </Box>
        </Card>
        <Card>
          <Box padding="400">
            <div className="preview-container">
              <div className="preview-header">
                <h3>Notify Me Modal Preview</h3>
              </div>
              <div style={{
                backgroundColor: currentSettings.notifyMeModalBackgroundColor || presalesDefaults.notifyMeModalBackgroundColor || '#ffffff',
                borderRadius: '12px',
                padding: '32px',
                maxWidth: '500px',
                width: '100%',
                boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)',
                position: 'relative',
                marginTop: '16px'
              }}>
                <h2 style={{
                  margin: '0 0 8px 0',
                  fontSize: '20px',
                  fontWeight: '600',
                  color: currentSettings.notifyMeModalTitleColor || presalesDefaults.notifyMeModalTitleColor || '#1a1a1a'
                }}>
                  {decodeForPreview(currentSettings.notifyMeModalTitle || presalesDefaults.notifyMeModalTitle || 'Notify Me')}
                </h2>
                <p style={{
                  margin: '0 0 24px 0',
                  fontSize: '12px',
                  color: currentSettings.notifyMeModalDescriptionColor || presalesDefaults.notifyMeModalDescriptionColor || '#666666',
                  lineHeight: '1.5'
                }}>
                  {decodeForPreview(currentSettings.notifyMeModalDescription || presalesDefaults.notifyMeModalDescription || 'Enter your email to be notified when this product becomes available.')}
                </p>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'stretch' }}>
                  <input
                    type="email"
                    placeholder={decodeForPreview(currentSettings.notifyMeEmailPlaceholder || presalesDefaults.notifyMeEmailPlaceholder || 'Enter your email')}
                    style={{
                      flex: 1,
                      padding: '12px 8px',
                      border: '2px solid #e0e0e0',
                      borderRadius: '8px',
                      fontSize: '12px',
                      outline: 'none',
                      boxSizing: 'border-box'
                    }}
                    readOnly
                  />
                  <button
                    style={{
                      padding: '12px 8px',
                      backgroundColor: currentSettings.notifyMeButtonBackgroundColor || presalesDefaults.notifyMeButtonBackgroundColor || '#4a90e2',
                      color: currentSettings.notifyMeButtonTextColor || presalesDefaults.notifyMeButtonTextColor || '#ffffff',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '12px',
                      fontWeight: '600',
                      cursor: 'default',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {decodeForPreview(currentSettings.notifyMeButtonText || presalesDefaults.notifyMeButtonText || 'Notify me')}
                  </button>
                </div>
              </div>
            </div>
          </Box>
        </Card>
      </>
    );
  }

  // Product Drop preview
  if (activeTab === 3) {
    return (
      <>
        <Card>
          <Box padding="400">
            <div className="preview-container">
              <div className="preview-header">
                <h3>Product Drop Widget Preview</h3>
              </div>
              <div className="preview-widget" style={{
                fontSize: '13px',
                padding: '16px',
                borderRadius: '8px'
              }}>
              
              <p style={{
                    fontSize: '12px',
                    fontWeight: '600',
                    margin: '0 0 12px 0',
                  }}>
                    Before live
                  </p>
                {/* Before Live State */}
                <div style={{
                  backgroundColor: currentSettings.backgroundColor || productDropDefaults.backgroundColor,
                  borderRadius: `${currentSettings.widgetBorderRadius || productDropDefaults.widgetBorderRadius}px`,
                  padding: '16px',
                  marginBottom: '16px',
                  border: `1px solid ${currentSettings.widgetBorderColor || productDropDefaults.widgetBorderColor || '#e1e5e9'}`
                }}>
                  

                  <h4 style={{
                    fontSize: `${currentSettings.headerFontSize || productDropDefaults.headerFontSize || 16}px`,
                    fontWeight: 'bold',
                    margin: '0 0 16px 0',
                    color: currentSettings.headerColor || productDropDefaults.headerColor
                  }}>
                    {decodeForPreview(currentSettings.beforeLiveTitle || productDropDefaults.beforeLiveTitle)}
                  </h4>

                  {/* Countdown Timer */}
                  <div style={{ textAlign: 'center', marginBottom: '16px' }}>
                    <div style={{ display: 'flex', gap: '4px', alignItems: 'center', justifyContent: 'center', marginBottom: '4px' }}>
                      <span style={{ fontSize: '24px', fontWeight: 'bold', color: currentSettings.timerTextColor || productDropDefaults.timerTextColor || '#000000' }}>
                        XX
                      </span>
                      <span style={{ fontSize: '24px', fontWeight: 'bold', color: currentSettings.timerTextColor || productDropDefaults.timerTextColor || '#000000', margin: '0 2px' }}>:</span>
                      <span style={{ fontSize: '24px', fontWeight: 'bold', color: currentSettings.timerTextColor || productDropDefaults.timerTextColor || '#000000' }}>
                        XX
                      </span>
                      <span style={{ fontSize: '24px', fontWeight: 'bold', color: currentSettings.timerTextColor || productDropDefaults.timerTextColor || '#000000', margin: '0 2px' }}>:</span>
                      <span style={{ fontSize: '24px', fontWeight: 'bold', color: currentSettings.timerTextColor || productDropDefaults.timerTextColor || '#000000' }}>
                        XX
                      </span>
                      <span style={{ fontSize: '24px', fontWeight: 'bold', color: currentSettings.timerTextColor || productDropDefaults.timerTextColor || '#000000', margin: '0 2px' }}>:</span>
                      <span style={{ fontSize: '24px', fontWeight: 'bold', color: currentSettings.timerTextColor || productDropDefaults.timerTextColor || '#000000' }}>
                        XX
                      </span>
                    </div>
                  </div>

                  {/* Notify Button */}
                  <button 
                    onClick={(e) => e.preventDefault()}
                    style={{
                      border: `2px solid ${currentSettings.buttonBackgroundColor || productDropDefaults.buttonBackgroundColor || '#000000'}`,
                      backgroundColor: currentSettings.buttonBackgroundColor || productDropDefaults.buttonBackgroundColor || '#000000',
                      color: currentSettings.buttonTextColor || productDropDefaults.buttonTextColor || '#ffffff',
                      borderRadius: currentSettings.buttonStyle === 'pill' ? '50px' :
                        currentSettings.buttonStyle === 'square' ? '0px' :
                          '8px',
                      padding: '5px 16px',
                      fontSize: `${currentSettings.buttonFontSize || productDropDefaults.buttonFontSize || 14}px`,
                      fontWeight: '600',
                      cursor: 'default',
                      width: '100%',
                      marginBottom: '12px'
                    }}>
                    {decodeForPreview(currentSettings.beforeLiveButton || productDropDefaults.beforeLiveButton)}
                  </button>

                  {/* Drop Details */}
                  <p style={{
                    margin: 0,
                    color: currentSettings.subtitleColor || productDropDefaults.subtitleColor,
                    fontSize: `${currentSettings.secondaryFontSize || productDropDefaults.secondaryFontSize || 13}px`
                  }}>
                    {formatTimeRemaining(
                      decodeForPreview(currentSettings.beforeLiveDetails || productDropDefaults.beforeLiveDetails)
                        .replace('{date}', new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }))
                        .replace('{time}', getCurrentTime())
                    )}
                  </p>
                </div>
                <p style={{
                    fontSize: '12px',
                    fontWeight: '600',
                    margin: '0 0 12px 0',
                  }}>
                    When live
                  </p>
                {/* When Live State */}
                <div style={{
                  backgroundColor: currentSettings.backgroundColor || productDropDefaults.backgroundColor,
                  borderRadius: `${currentSettings.widgetBorderRadius || productDropDefaults.widgetBorderRadius}px`,
                  padding: '16px',
                  marginBottom: '16px',
                  border: `1px solid ${currentSettings.widgetBorderColor || productDropDefaults.widgetBorderColor || '#e1e5e9'}`
                }}>
                  

                  <h4 style={{
                    fontSize: `${currentSettings.headerFontSize || productDropDefaults.headerFontSize || 16}px`,
                    fontWeight: 'bold',
                    margin: '0 0 16px 0',
                    color: currentSettings.headerColor || productDropDefaults.headerColor
                  }}>
                    {decodeForPreview(currentSettings.whenLiveTitle || productDropDefaults.whenLiveTitle)}
                  </h4>

                  {/* Progress Bar */}
                  <div style={{ marginBottom: '16px' }}>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '8px' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{
                          width: '100%',
                          height: '8px',
                          backgroundColor: currentSettings.progressBarBackgroundColor || productDropDefaults.progressBarBackgroundColor || '#e1e5e9',
                          borderRadius: '4px',
                          overflow: 'hidden'
                        }}>
                          <div style={{
                            width: '50%',
                            height: '100%',
                            backgroundColor: currentSettings.progressBarColor || productDropDefaults.progressBarColor,
                            transition: 'width 0.3s ease'
                          }} />
                        </div>
                      </div>
                      <span style={{ fontWeight: '600', fontSize: `${currentSettings.primaryFontSize || productDropDefaults.primaryFontSize || 14}px`, color: currentSettings.textColor || productDropDefaults.textColor }}>
                        {decodeForPreview(currentSettings.claimedPercentageText || productDropDefaults.claimedPercentageText || '{percentage}% Claimed').replace('{percentage}', 'XX')}
                      </span>
                    </div>
                    <p style={{
                      margin: 0,
                      color: currentSettings.subtitleColor || productDropDefaults.subtitleColor,
                      fontSize: `${currentSettings.primaryFontSize || productDropDefaults.primaryFontSize || 14}px`
                    }}>
                      {decodeForPreview(currentSettings.raisedText || productDropDefaults.raisedText).replace('{raised}', 'XX.XX')} / {decodeForPreview(currentSettings.goalText || productDropDefaults.goalText).replace('{goal}', 'XX.XX')}
                    </p>
                  </div>

                  {/* Buy Now Button */}
                  <button 
                    onClick={(e) => e.preventDefault()}
                    style={{
                      border: `2px solid ${currentSettings.buttonBackgroundColor || productDropDefaults.buttonBackgroundColor || '#000000'}`,
                      backgroundColor: currentSettings.buttonBackgroundColor || productDropDefaults.buttonBackgroundColor || '#000000',
                      color: currentSettings.buttonTextColor || productDropDefaults.buttonTextColor || '#ffffff',
                      borderRadius: currentSettings.buttonStyle === 'pill' ? '50px' :
                        currentSettings.buttonStyle === 'square' ? '0px' :
                          '8px',
                      padding: '5px 16px',
                      fontSize: `${currentSettings.buttonFontSize || productDropDefaults.buttonFontSize || 14}px`,
                      fontWeight: '600',
                      cursor: 'default',
                      width: '100%',
                      marginBottom: '12px'
                    }}>
                    {decodeForPreview(currentSettings.whenLiveButton || productDropDefaults.whenLiveButton)}
                  </button>

                  {/* Purchase Limit */}
                  <p style={{
                    margin: 0,
                    color: currentSettings.subtitleColor || productDropDefaults.subtitleColor,
                    fontSize: `${currentSettings.secondaryFontSize || productDropDefaults.secondaryFontSize || 13}px`
                  }}>
                    {decodeForPreview(currentSettings.whenLiveLimit || productDropDefaults.whenLiveLimit).replace('{limit}', 'XX')}
                  </p>
                </div>
                <p style={{
                    fontSize: '12px',
                    fontWeight: '600',
                    margin: '0 0 12px 0',
                  }}>
                    Sale ended
                  </p>
                {/* After Live State */}
                <div style={{
                  backgroundColor: currentSettings.backgroundColor || productDropDefaults.backgroundColor,
                  borderRadius: `${currentSettings.widgetBorderRadius || productDropDefaults.widgetBorderRadius}px`,
                  padding: '16px',
                  marginBottom: '16px',
                  border: `1px solid ${currentSettings.widgetBorderColor || productDropDefaults.widgetBorderColor || '#e1e5e9'}`
                }}>
                 

                  <h4 style={{
                    fontSize: `${currentSettings.headerFontSize || productDropDefaults.headerFontSize || 16}px`,
                    fontWeight: 'bold',
                    margin: '0 0 12px 0',
                    color: currentSettings.afterLiveTitleColor || productDropDefaults.afterLiveTitleColor || '#ef4444'
                  }}>
                    {decodeForPreview(currentSettings.afterLiveTitle || productDropDefaults.afterLiveTitle)}
                  </h4>

                  <p style={{
                    margin: '0 0 12px 0',
                    color: currentSettings.textColor || productDropDefaults.textColor,
                    fontSize: `${currentSettings.secondaryFontSize || productDropDefaults.secondaryFontSize || 13}px`
                  }}>
                    {decodeForPreview(currentSettings.afterLiveMessage || productDropDefaults.afterLiveMessage)}
                  </p>

                  {/* Notify for Restock Button */}
                  <button 
                    onClick={(e) => e.preventDefault()}
                    style={{
                      border: `2px solid ${currentSettings.buttonBackgroundColor || productDropDefaults.buttonBackgroundColor || '#000000'}`,
                      backgroundColor: currentSettings.buttonBackgroundColor || productDropDefaults.buttonBackgroundColor || '#000000',
                      color: currentSettings.buttonTextColor || productDropDefaults.buttonTextColor || '#ffffff',
                      borderRadius: currentSettings.buttonStyle === 'pill' ? '50px' :
                        currentSettings.buttonStyle === 'square' ? '0px' :
                          '8px',
                      padding: '5px 16px',
                      fontSize: `${currentSettings.buttonFontSize || productDropDefaults.buttonFontSize || 14}px`,
                      fontWeight: '600',
                      cursor: 'default',
                      width: '100%'
                    }}>
                    {decodeForPreview(currentSettings.afterLiveButton || productDropDefaults.afterLiveButton)}
                  </button>
                </div>
              </div>
            </div>
          </Box>
        </Card>
        <Card>
          <Box padding="400">
            <div className="preview-container">
              <div className="preview-header">
                <h3>Notify Me Modal Preview</h3>
              </div>
              <BlockStack gap="400">
                <div>
                  <p style={{ fontSize: '12px', fontWeight: '600', margin: '0 0 12px 0' }}>Before Live Modal</p>
                  <div style={{
                    backgroundColor: currentSettings.notifyMeBeforeModalBackgroundColor || productDropDefaults.notifyMeBeforeModalBackgroundColor || '#ffffff',
                    borderRadius: '12px',
                    padding: '32px',
                    maxWidth: '500px',
                    width: '100%',
                    boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)',
                    position: 'relative',
                    marginBottom: '16px'
                  }}>
                    <h2 style={{
                      margin: '0 0 8px 0',
                      fontSize: '20px',
                      fontWeight: '600',
                      color: currentSettings.notifyMeBeforeModalTitleColor || productDropDefaults.notifyMeBeforeModalTitleColor || '#1a1a1a'
                    }}>
                      {decodeForPreview(currentSettings.notifyMeBeforeModalTitle || productDropDefaults.notifyMeBeforeModalTitle || 'Notify Me')}
                    </h2>
                    <p style={{
                      margin: '0 0 24px 0',
                      fontSize: '12px',
                      color: currentSettings.notifyMeBeforeModalDescriptionColor || productDropDefaults.notifyMeBeforeModalDescriptionColor || '#666666',
                      lineHeight: '1.5'
                    }}>
                      {decodeForPreview(currentSettings.notifyMeBeforeModalDescription || productDropDefaults.notifyMeBeforeModalDescription || 'Enter your email to be notified when this product drops.')}
                    </p>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'stretch' }}>
                      <input
                        type="email"
                        placeholder={decodeForPreview(currentSettings.notifyMeBeforeEmailPlaceholder || productDropDefaults.notifyMeBeforeEmailPlaceholder || 'Enter your email')}
                        style={{
                          flex: 1,
                          padding: '12px 8px',
                          border: '2px solid #e0e0e0',
                          borderRadius: '8px',
                          fontSize: '12px',
                          outline: 'none',
                          boxSizing: 'border-box'
                        }}
                        readOnly
                      />
                      <button
                        style={{
                          padding: '12px 8px',
                          backgroundColor: currentSettings.notifyMeBeforeButtonBackgroundColor || productDropDefaults.notifyMeBeforeButtonBackgroundColor || '#4a90e2',
                          color: currentSettings.notifyMeBeforeButtonTextColor || productDropDefaults.notifyMeBeforeButtonTextColor || '#ffffff',
                          border: 'none',
                          borderRadius: '8px',
                          fontSize: '12px',
                          fontWeight: '600',
                          cursor: 'default',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {decodeForPreview(currentSettings.notifyMeBeforeButtonText || productDropDefaults.notifyMeBeforeButtonText || 'Notify me')}
                      </button>
                    </div>
                  </div>
                </div>
                <div>
                  <p style={{ fontSize: '12px', fontWeight: '600', margin: '0 0 12px 0' }}>After Live Modal (Restock)</p>
                  <div style={{
                    backgroundColor: currentSettings.notifyMeAfterModalBackgroundColor || productDropDefaults.notifyMeAfterModalBackgroundColor || '#ffffff',
                    borderRadius: '12px',
                    padding: '32px',
                    maxWidth: '500px',
                    width: '100%',
                    boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)',
                    position: 'relative'
                  }}>
                    <h2 style={{
                      margin: '0 0 8px 0',
                      fontSize: '20px',
                      fontWeight: '600',
                      color: currentSettings.notifyMeAfterModalTitleColor || productDropDefaults.notifyMeAfterModalTitleColor || '#1a1a1a'
                    }}>
                      {decodeForPreview(currentSettings.notifyMeAfterModalTitle || productDropDefaults.notifyMeAfterModalTitle || 'Notify Me')}
                    </h2>
                    <p style={{
                      margin: '0 0 24px 0',
                      fontSize: '12px',
                      color: currentSettings.notifyMeAfterModalDescriptionColor || productDropDefaults.notifyMeAfterModalDescriptionColor || '#666666',
                      lineHeight: '1.5'
                    }}>
                      {decodeForPreview(currentSettings.notifyMeAfterModalDescription || productDropDefaults.notifyMeAfterModalDescription || 'Enter your email to be notified when this product is restocked.')}
                    </p>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'stretch' }}>
                      <input
                        type="email"
                        placeholder={decodeForPreview(currentSettings.notifyMeAfterEmailPlaceholder || productDropDefaults.notifyMeAfterEmailPlaceholder || 'Enter your email')}
                        style={{
                          flex: 1,
                          padding: '12px 8px',
                          border: '2px solid #e0e0e0',
                          borderRadius: '8px',
                          fontSize: '12px',
                          outline: 'none',
                          boxSizing: 'border-box'
                        }}
                        readOnly
                      />
                      <button
                        style={{
                          padding: '12px 8px',
                          backgroundColor: currentSettings.notifyMeAfterButtonBackgroundColor || productDropDefaults.notifyMeAfterButtonBackgroundColor || '#4a90e2',
                          color: currentSettings.notifyMeAfterButtonTextColor || productDropDefaults.notifyMeAfterButtonTextColor || '#ffffff',
                          border: 'none',
                          borderRadius: '8px',
                          fontSize: '12px',
                          fontWeight: '600',
                          cursor: 'default',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {decodeForPreview(currentSettings.notifyMeAfterButtonText || productDropDefaults.notifyMeAfterButtonText || 'Notify me')}
                      </button>
                    </div>
                  </div>
                </div>
              </BlockStack>
            </div>
          </Box>
        </Card>
      </>
    );
  }

  return null;
}, (prevProps, nextProps) => {
  // Only re-render if activeTab or relevant settings changed
  return prevProps.activeTab === nextProps.activeTab &&
    JSON.stringify(prevProps.settings) === JSON.stringify(nextProps.settings);
});
PreviewComponent.displayName = 'PreviewComponent';

const Settings = memo(() => {
  const app = useSafeAppBridge();
  // Get install state from Redux (using reusable hook - ensures install API is only called once)
  const {
    installChecked,
    installSuccess,
    installLoading,
    installData,
    shop,
    host
  } = useEnsureInstall();

  const [activeTab, setActiveTab] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [showBanner, setShowBanner] = useState(false);
  const [bannerErrors, setBannerErrors] = useState([]);
  const formRef = useRef(null);
  const isSavingRef = useRef(false);
  const toastTimeoutRef = useRef(null);
  const settingsLoadedRef = useRef(null); // Track the clientId that was loaded to prevent duplicate API calls
  const [originalData, setOriginalData] = useState(null);

  const handleTabChange = useCallback((tabIndex) => {
    setActiveTab(tabIndex);
    // Don't hide banner when switching tabs - keep errors visible
  }, []);

  const showToastMessage = useCallback((message) => {
    if (toastTimeoutRef.current) {
      clearTimeout(toastTimeoutRef.current);
    }
    setToastMessage(message);
    setShowToast(true);
    toastTimeoutRef.current = setTimeout(() => {
      setShowToast(false);
      toastTimeoutRef.current = null;
    }, 5000);
  }, []);

  // Validation function to check for empty required fields across all tabs
  const validateSettings = useCallback((values) => {
    const errors = [];
    const checkEmpty = (value) => {
      return !value || (typeof value === 'string' && value.trim() === '');
    };

    // Crowdfunding validation
    const crowdfunding = values.crowdfunding || {};
    if (checkEmpty(crowdfunding.raisedText)) errors.push({ tab: 'Crowdfunding', field: 'Raised Amount Text' });
    if (checkEmpty(crowdfunding.goalText)) errors.push({ tab: 'Crowdfunding', field: 'Goal Amount Text' });
    if (checkEmpty(crowdfunding.backerText)) errors.push({ tab: 'Crowdfunding', field: 'Backer Count Text' });
    if (checkEmpty(crowdfunding.timeLeftText)) errors.push({ tab: 'Crowdfunding', field: 'Time Remaining Text' });
    if (checkEmpty(crowdfunding.progressMessage)) errors.push({ tab: 'Crowdfunding', field: 'Progress Message' });
    // Typography validation
    if (crowdfunding.headerFontSize === null || crowdfunding.headerFontSize === undefined || crowdfunding.headerFontSize === '') {
      errors.push({ tab: 'Crowdfunding', field: 'Header Font Size' });
    }
    if (crowdfunding.primaryFontSize === null || crowdfunding.primaryFontSize === undefined || crowdfunding.primaryFontSize === '') {
      errors.push({ tab: 'Crowdfunding', field: 'Primary Text Font Size' });
    }
    if (crowdfunding.secondaryFontSize === null || crowdfunding.secondaryFontSize === undefined || crowdfunding.secondaryFontSize === '') {
      errors.push({ tab: 'Crowdfunding', field: 'Secondary Text Font Size' });
    }
    if (crowdfunding.buttonFontSize === null || crowdfunding.buttonFontSize === undefined || crowdfunding.buttonFontSize === '') {
      errors.push({ tab: 'Crowdfunding', field: 'Button Font Size' });
    }

    // Presales validation
    const presales = values.presales || {};
    if (checkEmpty(presales.preOrderText)) errors.push({ tab: 'Presales', field: 'Pre-order Text' });
    if (checkEmpty(presales.backerText)) errors.push({ tab: 'Presales', field: 'Pre-orderer Text' });
    if (checkEmpty(presales.timeLeftText)) errors.push({ tab: 'Presales', field: 'Time Left Text' });
    if (checkEmpty(presales.buttonText)) errors.push({ tab: 'Presales', field: 'Button Text' });
    if (checkEmpty(presales.whenLiveTitle)) errors.push({ tab: 'Presales', field: 'When Live Title' });
    if (checkEmpty(presales.whenLiveButton)) errors.push({ tab: 'Presales', field: 'When Live Button' });
    if (checkEmpty(presales.afterLiveTitle)) errors.push({ tab: 'Presales', field: 'After Live Title' });
    if (checkEmpty(presales.afterLiveButton)) errors.push({ tab: 'Presales', field: 'After Live Button' });
    if (checkEmpty(presales.afterLiveMessage)) errors.push({ tab: 'Presales', field: 'After Live Message' });
    // Typography validation
    if (presales.headerFontSize === null || presales.headerFontSize === undefined || presales.headerFontSize === '') {
      errors.push({ tab: 'Presales', field: 'Header Font Size' });
    }
    if (presales.primaryFontSize === null || presales.primaryFontSize === undefined || presales.primaryFontSize === '') {
      errors.push({ tab: 'Presales', field: 'Primary Text Font Size' });
    }
    if (presales.secondaryFontSize === null || presales.secondaryFontSize === undefined || presales.secondaryFontSize === '') {
      errors.push({ tab: 'Presales', field: 'Secondary Text Font Size' });
    }
    if (presales.buttonFontSize === null || presales.buttonFontSize === undefined || presales.buttonFontSize === '') {
      errors.push({ tab: 'Presales', field: 'Button Font Size' });
    }

    // Product Drop validation
    const productDrop = values.productDrop || {};
    if (checkEmpty(productDrop.goalText)) errors.push({ tab: 'Product Drop', field: 'Goal Text' });
    if (checkEmpty(productDrop.raisedText)) errors.push({ tab: 'Product Drop', field: 'Sold Text' });
    if (checkEmpty(productDrop.backerText)) errors.push({ tab: 'Product Drop', field: 'Buyer Text' });
    if (checkEmpty(productDrop.timeLeftText)) errors.push({ tab: 'Product Drop', field: 'Time Left Text' });
    if (checkEmpty(productDrop.buttonText)) errors.push({ tab: 'Product Drop', field: 'Button Text' });
    if (checkEmpty(productDrop.beforeLiveTitle)) errors.push({ tab: 'Product Drop', field: 'Before Live Title' });
    if (checkEmpty(productDrop.beforeLiveButton)) errors.push({ tab: 'Product Drop', field: 'Before Live Button' });
    if (checkEmpty(productDrop.beforeLiveDetails)) errors.push({ tab: 'Product Drop', field: 'Before Live Details' });
    if (checkEmpty(productDrop.whenLiveTitle)) errors.push({ tab: 'Product Drop', field: 'When Live Title' });
    if (checkEmpty(productDrop.whenLiveButton)) errors.push({ tab: 'Product Drop', field: 'When Live Button' });
    if (checkEmpty(productDrop.whenLiveLimit)) errors.push({ tab: 'Product Drop', field: 'Purchase Limit Text' });
    if (checkEmpty(productDrop.afterLiveTitle)) errors.push({ tab: 'Product Drop', field: 'After Live Title' });
    if (checkEmpty(productDrop.afterLiveButton)) errors.push({ tab: 'Product Drop', field: 'After Live Button' });
    if (checkEmpty(productDrop.afterLiveMessage)) errors.push({ tab: 'Product Drop', field: 'After Live Message' });
    // Typography validation
    if (productDrop.headerFontSize === null || productDrop.headerFontSize === undefined || productDrop.headerFontSize === '') {
      errors.push({ tab: 'Product Drop', field: 'Header Font Size' });
    }
    if (productDrop.primaryFontSize === null || productDrop.primaryFontSize === undefined || productDrop.primaryFontSize === '') {
      errors.push({ tab: 'Product Drop', field: 'Primary Text Font Size' });
    }
    if (productDrop.secondaryFontSize === null || productDrop.secondaryFontSize === undefined || productDrop.secondaryFontSize === '') {
      errors.push({ tab: 'Product Drop', field: 'Secondary Text Font Size' });
    }
    if (productDrop.buttonFontSize === null || productDrop.buttonFontSize === undefined || productDrop.buttonFontSize === '') {
      errors.push({ tab: 'Product Drop', field: 'Button Font Size' });
    }

    return errors;
  }, []);

  // Function to clean and validate settings object - only include valid fields from INITIAL_VALUES
  const cleanSettingsObject = useCallback((values) => {
    if (!values) return INITIAL_VALUES;
    
    // Get all valid field keys from INITIAL_VALUES
    const crowdfundingKeys = Object.keys(INITIAL_VALUES.crowdfunding);
    const presalesKeys = Object.keys(INITIAL_VALUES.presales);
    const productDropKeys = Object.keys(INITIAL_VALUES.productDrop);
    
    // Filter to only include valid fields and ensure all required fields are present
    const cleanedCrowdfunding = {};
    crowdfundingKeys.forEach(key => {
      // Use value if exists and is valid, otherwise use default
      const value = values.crowdfunding?.[key];
      cleanedCrowdfunding[key] = (value !== null && value !== undefined && value !== '') 
        ? value 
        : INITIAL_VALUES.crowdfunding[key];
    });
    
    const cleanedPresales = {};
    presalesKeys.forEach(key => {
      const value = values.presales?.[key];
      cleanedPresales[key] = (value !== null && value !== undefined && value !== '') 
        ? value 
        : INITIAL_VALUES.presales[key];
    });
    
    const cleanedProductDrop = {};
    productDropKeys.forEach(key => {
      const value = values.productDrop?.[key];
      cleanedProductDrop[key] = (value !== null && value !== undefined && value !== '') 
        ? value 
        : INITIAL_VALUES.productDrop[key];
    });
    
    return {
      crowdfunding: cleanedCrowdfunding,
      presales: cleanedPresales,
      productDrop: cleanedProductDrop
    };
  }, []);

  const formik = useFormik({
    initialValues: INITIAL_VALUES,
    enableReinitialize: true,
    onSubmit: async (values) => {
      if (isSavingRef.current) return;
      if (!installChecked || !installData) {
        showToastMessage('Please wait for app installation check to complete');
        return;
      }
      
      isSavingRef.current = true;
      try {
        // Clean settings to only include valid fields
        const cleanedValues = cleanSettingsObject(values);
        
        // Encode HTML entities for storage
        const encodedValues = {
          crowdfunding: encodeHtmlEntitiesDeep(cleanedValues.crowdfunding),
          presales: encodeHtmlEntitiesDeep(cleanedValues.presales),
          productDrop: encodeHtmlEntitiesDeep(cleanedValues.productDrop)
        };
        
        // Prepare API payload
        const apiPayload = {
          client_id: installData?.shop_info?.id || 1,
          crowdfunding_settings: encodedValues.crowdfunding,
          presales_settings: encodedValues.presales,
          product_drop_settings: encodedValues.productDrop,
          custom_css: values.customCss || '',
          custom_script: values.customScript || ''
        };

        const shopifyId = installData?.shop_info?.shopify_id;
        
        // OPTIMIZATION: Call API and GraphQL metafield update simultaneously for faster save
        const apiSavePromise = campaignAPI.saveSettings(apiPayload);
        
        // Prepare metafield save promise if shopifyId is available (run in parallel)
        let metafieldSavePromise = null;
        if (shopifyId) {
          // Import batch function for efficient metafield updates
          const { batchCreateOrUpdateShopMetafields } = await import('../helper/metafieldHelpers');
          metafieldSavePromise = batchCreateOrUpdateShopMetafields(
            encodedValues,
            values.customCss || '',
            values.customScript || '',
            shopifyId
          ).catch(error => {
            console.warn('Failed to save metafields (non-critical):', error);
            // Don't throw - metafield save is secondary to API save
            return { success: false, error };
          });
        }
        
        // Execute saves in parallel (API is required, metafield is optional)
        const [apiResponse, metafieldResult] = await Promise.all([
          apiSavePromise,
          metafieldSavePromise || Promise.resolve(null)
        ]);
        
        if (apiResponse.success) {
          // Save to localStorage including custom CSS and Script
          // Note: customCss and customScript should NOT be HTML-encoded since they're code
          const localStorageData = {
            ...encodedValues,
            customCss: values.customCss || '',
            customScript: values.customScript || ''
          };
          localStorage.setItem('campaign-settings', JSON.stringify(localStorageData));
          // Update originalData to current values (saved state) without resetting form
          setOriginalData(JSON.parse(JSON.stringify(values)));
          // Don't reset form - keep current values as they are
          // Hide banner on successful save
          setShowBanner(false);
          setBannerErrors([]);
          showToastMessage('Save successfully');
        } else {
          throw new Error(apiResponse.message || 'Failed to save settings');
        }
      } catch (error) {
        console.error('Error saving settings:', error);
        showToastMessage('Failed to save settings');
      } finally {
        isSavingRef.current = false;
      }
    },
  });

  // Use deferred value for preview to reduce re-renders during typing
  const deferredValues = useDeferredValue(formik.values);

  const handleSave = useCallback(async () => {
    // Validate all tabs before saving
    const errors = validateSettings(formik.values);
    
    if (errors.length > 0) {
      setBannerErrors(errors);
      setShowBanner(true);
      // Scroll to top to show banner
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    
    // Don't hide banner here - it will be hidden on successful save in onSubmit
    await formik.handleSubmit();
    // Toast will be shown after successful save in onSubmit
  }, [formik, validateSettings]);

  const handleDiscard = useCallback(() => {
    if (originalData) {
      formik.setValues(originalData);
      formik.resetForm({ values: originalData });
    } else {
      formik.resetForm();
    }
  }, [originalData, formik.setValues, formik.resetForm]);

  // Helper function to check if item is an object
  const isObject = (item) => {
    return item && typeof item === 'object' && !Array.isArray(item);
  };

  // Helper function to deep merge objects
  const deepMerge = (target, source) => {
    const output = { ...target };
    if (isObject(target) && isObject(source)) {
      Object.keys(source).forEach(key => {
        if (isObject(source[key])) {
          if (!(key in target)) {
            Object.assign(output, { [key]: source[key] });
          } else {
            output[key] = deepMerge(target[key], source[key]);
          }
        } else {
          Object.assign(output, { [key]: source[key] });
        }
      });
    }
    return output;
  };

  useEffect(() => {
    // OPTIMIZATION: Only load from API during initial load - NO GraphQL calls
    // Load settings only when install data is available
    // Prevent duplicate API calls by checking if settings have already been loaded for this clientId
    if (!installChecked || !installData) return;
    
    const clientId = installData?.shop_info?.id || 1;
    
    // Prevent duplicate calls for the same clientId
    if (settingsLoadedRef.current === clientId) return;
    
    // Mark this clientId as loading to prevent duplicate calls
    settingsLoadedRef.current = clientId;
    
    const loadSettings = async () => {
      setIsLoading(true);
      try {
        let formikValues = null;
        
        // Only load from API (primary source) - NO GraphQL calls during load
        try {
          const response = await campaignAPI.getSettings(clientId);
          
          if (response.success && response.data) {
            const apiData = response.data;
            
            // Merge API data with defaults to ensure all fields are populated
            const mergedValues = {
              crowdfunding: deepMerge(
                INITIAL_VALUES.crowdfunding,
                decodeHtmlEntitiesDeep(apiData.crowdfunding_settings || {})
              ),
              presales: deepMerge(
                INITIAL_VALUES.presales,
                decodeHtmlEntitiesDeep(apiData.presales_settings || {})
              ),
              productDrop: deepMerge(
                INITIAL_VALUES.productDrop,
                decodeHtmlEntitiesDeep(apiData.product_drop_settings || {})
              )
            };
            
            // Clean merged values to ensure only valid fields are present
            formikValues = cleanSettingsObject(mergedValues);
            
            // Initialize custom CSS and Script from API response (from DB)
            // These are stored directly in the settings table, not in the nested settings objects
            // Decode HTML entities to show proper characters (e.g., &#039; -> ')
            formikValues.customCss = '';
            formikValues.customScript = '';
            
            if (apiData.custom_css !== undefined && apiData.custom_css !== null) {
              const cssValue = apiData.custom_css === 'false' || apiData.custom_css === false ? '' : String(apiData.custom_css);
              formikValues.customCss = decodeHtmlEntities(cssValue);
            }
            if (apiData.custom_script !== undefined && apiData.custom_script !== null) {
              const scriptValue = apiData.custom_script === 'false' || apiData.custom_script === false ? '' : String(apiData.custom_script);
              formikValues.customScript = decodeHtmlEntities(scriptValue);
            }
            
          }
        } catch (apiError) {
          console.warn('Failed to load from API, trying localStorage:', apiError);
          
          // If API fails, try localStorage (fallback only - NO GraphQL)
          try {
            const stored = localStorage.getItem('campaign-settings');
            if (stored) {
              const loadedSettings = JSON.parse(stored);
              const mergedFromLocalStorage = {
                crowdfunding: deepMerge(
                  INITIAL_VALUES.crowdfunding,
                  decodeHtmlEntitiesDeep(loadedSettings.crowdfunding || {})
                ),
                presales: deepMerge(
                  INITIAL_VALUES.presales,
                  decodeHtmlEntitiesDeep(loadedSettings.presales || {})
                ),
                productDrop: deepMerge(
                  INITIAL_VALUES.productDrop,
                  decodeHtmlEntitiesDeep(loadedSettings.productDrop || {})
                )
              };
              // Clean merged values to ensure only valid fields are present
              formikValues = cleanSettingsObject(mergedFromLocalStorage);
              
              // Get custom CSS and Script from localStorage if available
              // Decode HTML entities to show proper characters (e.g., &#039; -> ')
              const storedCss = loadedSettings.customCss || '';
              const storedScript = loadedSettings.customScript || '';
              formikValues.customCss = typeof storedCss === 'string' ? decodeHtmlEntities(storedCss) : '';
              formikValues.customScript = typeof storedScript === 'string' ? decodeHtmlEntities(storedScript) : '';
              
            }
          } catch (localError) {
            console.warn('Failed to load from localStorage:', localError);
          }
        }
        
        // Use loaded values or defaults, then clean to ensure only valid fields
        const finalValues = cleanSettingsObject(formikValues || INITIAL_VALUES);
        
        // Ensure custom CSS and Script are always strings (preserve values from API/localStorage)
        // Decode HTML entities if they exist (values should already be decoded above, but ensure they are)
        if (!formikValues || formikValues.customCss === undefined || formikValues.customCss === null) {
          finalValues.customCss = '';
        } else {
          const cssValue = String(formikValues.customCss);
          // Decode HTML entities to ensure proper display (e.g., &#039; -> ')
          finalValues.customCss = decodeHtmlEntities(cssValue);
        }
        
        if (!formikValues || formikValues.customScript === undefined || formikValues.customScript === null) {
          finalValues.customScript = '';
        } else {
          const scriptValue = String(formikValues.customScript);
          // Decode HTML entities to ensure proper display (e.g., &#039; -> ')
          finalValues.customScript = decodeHtmlEntities(scriptValue);
        }
        
        // Set formik values
        formik.setValues(finalValues);
        setOriginalData(JSON.parse(JSON.stringify(finalValues)));
        
        // Update localStorage with merged values
        // Note: customCss and customScript should NOT be HTML-encoded since they're code
        const localStorageData = {
          crowdfunding: encodeHtmlEntitiesDeep(finalValues.crowdfunding),
          presales: encodeHtmlEntitiesDeep(finalValues.presales),
          productDrop: encodeHtmlEntitiesDeep(finalValues.productDrop),
          customCss: finalValues.customCss || '', // Don't encode - it's code
          customScript: finalValues.customScript || '' // Don't encode - it's code
        };
        localStorage.setItem('campaign-settings', JSON.stringify(localStorageData));
        
      } catch (error) {
        console.error('Error loading settings:', error);
        // Reset the ref on error so it can retry if needed
        if (settingsLoadedRef.current === clientId) {
          settingsLoadedRef.current = null;
        }
        // Fallback to defaults
        const defaultValues = {
          ...INITIAL_VALUES,
          customCss: '',
          customScript: ''
        };
        formik.setValues(defaultValues);
        setOriginalData(JSON.parse(JSON.stringify(defaultValues)));
        // Save to localStorage - don't encode CSS and Script
        const localStorageData = {
          crowdfunding: encodeHtmlEntitiesDeep(defaultValues.crowdfunding),
          presales: encodeHtmlEntitiesDeep(defaultValues.presales),
          productDrop: encodeHtmlEntitiesDeep(defaultValues.productDrop),
          customCss: '',
          customScript: ''
        };
        localStorage.setItem('campaign-settings', JSON.stringify(localStorageData));
      } finally {
        setIsLoading(false);
      }
    };

    loadSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [installChecked, installData?.shop_info?.id]);

  useEffect(() => {
    if (installChecked && !installSuccess && !installData && shop) {
      const redirectUrl = buildNodeApiEntryUrl(shop, host);

      if (app) {
        try {
          const redirect = Redirect.create(app);
          redirect.dispatch(Redirect.Action.REMOTE, redirectUrl);
          return;
        } catch (error) {
          console.warn('[Settings] App Bridge redirect failed, trying fallback');
        }
      }

      try {
        if (window.top && window.top !== window) {
          window.top.location.href = redirectUrl;
        } else {
          window.location.href = redirectUrl;
        }
      } catch (error) {
        window.open(redirectUrl, '_blank');
      }
    }
  }, [installChecked, installSuccess, installData, shop, host, app]);

  useEffect(() => {
    return () => {
      if (toastTimeoutRef.current) {
        clearTimeout(toastTimeoutRef.current);
      }
    };
  }, []);

  const handleFormSubmit = useCallback((event) => {
    event.preventDefault();
    handleSave();
  }, [handleSave]);

  const handleFormReset = useCallback((event) => {
    event.preventDefault();
    handleDiscard();
  }, [handleDiscard]);

  const handleColorChange = useCallback((fieldPath, value) => {
    formik.setFieldValue(fieldPath, value);
  }, [formik]);

  const handleNumberChange = useCallback((fieldPath, value) => {
    // Allow empty string during typing - preserve it completely
    if (value === '' || value === null || value === undefined) {
      formik.setFieldValue(fieldPath, '');
      // Clear any existing errors when user is typing
      formik.setFieldError(fieldPath, undefined);
      return;
    }
    // Allow any number including 0, negative numbers, etc. - no constraints
    const parsed = parseInt(value);
    // If NaN, keep as empty string to allow user to continue typing
    if (isNaN(parsed)) {
      formik.setFieldValue(fieldPath, '');
      return;
    }
    formik.setFieldValue(fieldPath, parsed);
    // Clear error if valid number entered
    formik.setFieldError(fieldPath, undefined);
  }, [formik]);

  const handleNumberBlur = useCallback((fieldPath, value) => {
    // Validate: field cannot be blank
    if (value === '' || value === null || value === undefined) {
      formik.setFieldValue(fieldPath, null);
      formik.setFieldError(fieldPath, 'This field is required');
      return;
    }
    const parsed = parseInt(value);
    if (isNaN(parsed)) {
      formik.setFieldValue(fieldPath, null);
      formik.setFieldError(fieldPath, 'Please enter a valid number');
      return;
    }
    // Clear error if valid
    formik.setFieldError(fieldPath, undefined);
    formik.setFieldValue(fieldPath, parsed);
  }, [formik]);

  // Memoize current settings based on active tab - optimized to only depend on specific values
  const currentSettings = useMemo(() => {
    switch (activeTab) {
      case 1: return formik.values.crowdfunding;
      case 2: return formik.values.presales;
      case 3: return formik.values.productDrop;
      default: return formik.values.crowdfunding;
    }
  }, [activeTab, formik.values.crowdfunding, formik.values.presales, formik.values.productDrop]);

  // Memoize path prefix
  const pathPrefix = useMemo(() => {
    return activeTab === 1 ? 'crowdfunding' : activeTab === 2 ? 'presales' : 'productDrop';
  }, [activeTab]);

  // Memoize common props to prevent unnecessary re-renders
  const commonProps = useMemo(() => ({
    pathPrefix,
    onFieldChange: formik.setFieldValue,
    onColorChange: handleColorChange,
    onNumberChange: handleNumberChange,
    onNumberBlur: handleNumberBlur
  }), [pathPrefix, formik.setFieldValue, handleColorChange, handleNumberChange, handleNumberBlur]);

  // Render tab content - optimized to prevent unnecessary re-renders
  const renderTabContent = useMemo(() => {
    switch (activeTab) {
      case 1:
        return <CrowdfundingFormSection settings={currentSettings} {...commonProps} />;
      case 2:
        return <PresalesFormSectionWithModal settings={currentSettings} {...commonProps} />;
      case 3:
        return <ProductDropFormSectionWithModal settings={currentSettings} {...commonProps} />;
      default:
        return null;
    }
  }, [activeTab, currentSettings, commonProps]);

  // OPTIMIZATION: If install data already exists in Redux, use it immediately
  // Don't wait for installLoading if data is already available
  // The checkInstall thunk will skip API call if data exists
  if (installChecked && !installSuccess && !installData) {
    return null;
  }

  // If install data exists, render immediately (don't wait for loading)
  // If no data exists, wait for install check to complete
  if (!installData) {
    if (!installChecked || installLoading) {
      return null;
    }
  }

  if (isLoading) {
    return (
      <>
        <PageTitleBar />
        <Page>
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
          <Box padding="800" textAlign="center">
            <Spinner size="large" />
          </Box>
          </div>
        </Page>
      </>
    );
  }

  return (
    <>
      <PageTitleBar title="Settings" />
      <Page>
        {showBanner && bannerErrors.length > 0 && (
          <Box paddingBlockEnd="400">
            <Banner
              tone="critical"
              title="Please fill in all required fields"
              onDismiss={() => setShowBanner(false)}
            >
              <p>The following fields are required and cannot be empty:</p>
              <ul style={{ marginTop: '8px', marginBottom: 0, paddingLeft: '20px' }}>
                {bannerErrors.map((error, index) => (
                  <li key={index}>
                    <strong>{error.tab}:</strong> {error.field}
                  </li>
                ))}
              </ul>
            </Banner>
          </Box>
        )}
        <form
          ref={formRef}
          data-save-bar
          data-discard-confirmation
          onSubmit={handleFormSubmit}
          onReset={handleFormReset}
        >
          <BlockStack gap="500">
            <InlineStack gap="500" align="start" wrap={false}>
              <Box style={{ flex: '0 0 60%', minWidth: 0 }}>
                <BlockStack gap="300">
                    <Box padding="100">
                      <BlockStack gap="300">
                        <Text variant="headingMd" as="h3">
                          Campaign Type Settings
                        </Text>
                        <InlineStack gap="0" wrap={false}>
                          {TAB_OPTIONS.slice(1).map((tab, index) => {
                            const tabIndex = index + 1;
                            return (
                              <Box key={tab.id} style={{ flex: 1 }}>
                                <Button
                                  variant={activeTab === tabIndex ? "primary" : "tertiary"}
                                  onClick={() => handleTabChange(tabIndex)}
                                  fullWidth
                                >
                                  {tab.label}
                                </Button>
                              </Box>
                            );
                          })}
                        </InlineStack>
                      </BlockStack>
                    </Box>
                    <div
                      style={{
                        marginBottom: '1rem'
                      }}
                    >
                      {renderTabContent}
                    </div>
                    
                    {/* Custom CSS and Script Section */}
                    <CustomCssScriptSection
                      customCss={formik.values.customCss}
                      customScript={formik.values.customScript}
                      onCssChange={(value) => formik.setFieldValue('customCss', value)}
                      onScriptChange={(value) => formik.setFieldValue('customScript', value)}
                    />
                </BlockStack>
              </Box>

              <Box style={{ flex: '0 0 40%', minWidth: 0 }}>
                
                <Box
                  style={{
                    position: 'sticky',
                    top: '20px',
                    alignSelf: 'flex-start',
                    marginBottom: '1rem'
                  }}
                >
                  <BlockStack gap="400">
                    <PreviewComponent activeTab={activeTab} settings={deferredValues} />
                  </BlockStack>
                </Box>
              </Box>
            </InlineStack>
          </BlockStack>
        </form>
        
        {showToast && (
          <Toast
            content={toastMessage}
            onDismiss={() => setShowToast(false)}
          />
        )}
      </Page>
    </>
  );
});

Settings.displayName = 'Settings';

export default Settings;
